import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, GeoJSON, ImageOverlay, Pane, ZoomControl, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-lasso';

import { fetchCountries } from '../utils/countries.js';
import DrawLayer from './DrawLayer.jsx';
import SplitLayer from './SplitLayer.jsx';
import { colorForRegion, colorForBereich, UNCLASSIFIED_COLOR, NOTABLE_GOLD, VDP_GG_BLACK } from '../data/regionColors.js';
import { supabase } from '../lib/supabase.js';
import { isMobile } from '../utils/isMobile.js';
import { isKeyVineyard, drillStackAt, drillTargetAt } from '../utils/drillStack.js';
import { diffCulled } from '../utils/cullDiff.js';
import { borderWeightForZoom, dashForZoom, fillOpacityForZoom, dotWeightForZoom, heliStyleForZoom } from '../utils/zoomStyle.js';
import { BASEMAP_STYLE, BASEMAP_LABELS_STYLE, BASEMAP_PROBE_URL } from '../data/basemapStyles.js';
import { ATLAS } from '../data/atlasConfig.js';
import { regionTerm } from '../utils/terms.js';
import { probeTiles } from '../utils/tilesProbe.js';
import { GEO_RIVERS_STYLE, KEY_RIVERS, RIVER_NAME, RIVER_ALIASES } from '../data/geoFeatures.js';
import GeoMarkers, { geoObstacleRects } from './GeoMarkers.jsx';

// Lazy like FlyThrough: keeps maplibre-gl (+pmtiles) out of the main bundle,
// and Carto-fallback users never download the GL engine at all. The map
// background shows for the beat the chunk takes — same feel as raster tiles
// arriving.
const BasemapGL = lazy(() => import('./BasemapGL.jsx'));

// Tighter bounds focused on the wine regions (south/central Germany)
const GERMANY_BOUNDS = ATLAS.fitBounds;

// Per-layer data source for climate overlays (DWD for climate, Copernicus for
// elevation). Used in the tile attribution and the on-map legend credit.
// Every copyright the map can owe, in one always-visible line — layers
// toggle on and off, the credits don't. Per-layer attribution props stay
// empty so the bar never duplicates. dl-de/by-2-0 covers the German
// state/agency geodata (LGL BW, LVermGeo RLP, Bayer. VermV, LGRB, DLR RLP).
// Escape hatch while the self-hosted basemap proves itself: ?basemap=carto
// (or localStorage weinmap:basemap = 'carto') swaps the PMTiles GL basemap
// back to the Carto raster tiles. Read once — a manual choice doesn't
// hot-swap; the automatic probe fallback below can still flip it later.
const CARTO_FORCED = (() => {
  try {
    return new URLSearchParams(window.location.search).get('basemap') === 'carto'
      || window.localStorage.getItem('weinmap:basemap') === 'carto';
  } catch {
    return false;
  }
})();

// No WebGL (old devices, GPU-less headless browsers) means no MapLibre —
// the Carto rasters are the only basemap that can render at all.
const WEBGL_OK = (() => {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
})();

// One reachability probe per page load, shared across MapView (re)mounts:
// if the tileset's TileJSON (the first thing MapLibre itself fetches)
// can't answer, the map falls back to Carto instead of a blank basemap.
let _tilesProbe = null;
const probeSelfHostedTiles = () => {
  _tilesProbe ??= probeTiles(BASEMAP_PROBE_URL);
  return _tilesProbe;
};

// CARTO appears only when the raster fallback actually renders; the
// normal basemap (incl. the Natural Earth world backdrop) is all ours.
const staticAttribution = (carto) =>
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  + (carto
    ? ' &middot; &copy; <a href="https://carto.com/attributions">CARTO</a>'
    : ' &middot; &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a>'
      + ' &middot; tiles &copy; <a href="https://www.linkedin.com/in/mike-rodden/">Mike Rodden</a>')
  + ' &middot; &copy; <a href="https://www.esri.com/">Esri</a>, Maxar, USGS'
  + ' &middot; <a href="https://www.dwd.de/">DWD</a>'
  + ' &middot; <a href="https://dataspace.copernicus.eu/">Copernicus</a>'
  + ' &middot; <a href="https://www.lgl-bw.de/">LGL BW</a>,'
  + ' <a href="https://lvermgeo.rlp.de/">LVermGeo RLP</a>,'
  + ' <a href="https://www.ldbv.bayern.de/">Bayer. VermV</a>,'
  + ' <a href="https://maps.lgrb-bw.de/">LGRB</a>,'
  + ' <a href="https://www.dlr.rlp.de/">DLR RLP</a> (dl-de/by-2-0)';

// Registers the combined credit line once, independent of layer toggles.
// The basemap credit follows what actually renders (Carto vs self-hosted).
function StaticAttribution({ carto }) {
  const map = useMap();
  useEffect(() => {
    const line = staticAttribution(carto);
    map.attributionControl?.addAttribution(line);
    return () => map.attributionControl?.removeAttribution(line);
  }, [map, carto]);
  return null;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Stable reference: a new array per render would remount the rivers GL layer.
const GEO_RIVER_HIT_LAYERS = ['geo-river-line-low', 'geo-river-line', 'geo-river-label'];

// Interpolate a color ramp ([{value,color:'#rrggbb'}]) at a value (clamped).
const _hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
function colorAt(stops, v) {
  const lo = stops[0].value, hi = stops[stops.length - 1].value;
  v = Math.max(lo, Math.min(hi, v));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (v >= a.value && v <= b.value) {
      const t = (v - a.value) / ((b.value - a.value) || 1);
      const ca = _hex(a.color), cb = _hex(b.color);
      return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(ca[1] + (cb[1] - ca[1]) * t)},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
    }
  }
  return stops[stops.length - 1].color;
}

// Names/messages rendered via L.divIcon({ html }) or bindTooltip come from
// user-editable DB fields — escape them to prevent stored XSS.
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Padding options that offset for sidebar overlay in brand mode (desktop only)
const SIDEBAR_PX = 400;
function padOpts(p, extra = {}) {
  const v = typeof p === 'number' ? p : 30;
  const isDesktop = window.innerWidth > 800;
  // Mobile: the map runs full-height under the bottom sheet, so fitted bounds
  // need bottom clearance for the sheet at its default (mid, 42dvh) height.
  if (!isDesktop) return { paddingTopLeft: [v, v], paddingBottomRight: [v, v + Math.round(window.innerHeight * 0.42)], ...extra };
  return { paddingTopLeft: [v, v], paddingBottomRight: [v + SIDEBAR_PX, v], ...extra };
}

function MapReady({ children }) {
  const map = useMap();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const container = map.getContainer();
    const checkSize = () => {
      map.invalidateSize();
      const size = map.getSize();
      if (size.x > 100 && size.y > 100) {
        setReady(true);
        return true;
      }
      return false;
    };

    // Immediate check in case container is already sized
    if (checkSize()) return;

    // Observe container for late CSS layout changes
    const observer = new ResizeObserver(() => {
      if (checkSize()) observer.disconnect();
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [map]);

  if (!ready) return null;
  return children;
}

function MaskLayer({ mask, style }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!mask) return;
    try {
      const layer = L.geoJSON(mask, { style, interactive: false, pane: 'mask' });
      layer.addTo(map);
      layerRef.current = layer;
    } catch (e) {
      console.warn('[MaskLayer] failed to add mask:', e.message);
    }
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [mask, map]);

  // The style is zoom-dependent (the mask dissolves at far zoomouts) —
  // restyle in place, the mount effect only reads style once.
  useEffect(() => {
    layerRef.current?.setStyle(style);
  }, [style]);

  return null;
}

// setView that centers the point in the *visible* map area: desktop offsets
// for the right sidebar; mobile offsets for the bottom sheet (reads its live
// top edge, so any snap height works).
function setViewVisibleCenter(map, latlng, zoom) {
  const pt = map.project(latlng, zoom);
  if (window.innerWidth > 800) {
    pt.x += SIDEBAR_PX / 2;
  } else {
    const sheetTop = document.querySelector('.sidebar-wrap')?.getBoundingClientRect().top;
    const vv = window.visualViewport;
    const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const visibleBottom = Math.min(sheetTop ?? viewportBottom, viewportBottom);
    pt.y += (map.getSize().y / 2) - (visibleBottom / 2);
  }
  map.setView(map.unproject(pt, zoom), zoom, { animate: false });
}

// Instant snap to search target — no animation so heavy maps (many vineyards in viewport) don't
// thrash Leaflet's per-frame polygon transforms during a long flyTo, which caused GPU/compositor
// exhaustion on repeat searches.
// Mobile: the tap that triggers this usually also dismisses the on-screen
// keyboard, which resizes the visual viewport over the next frames. Centering
// immediately would frame the target for the keyboard-shrunken viewport and
// the held view would then sit wrong once the keyboard is gone — so wait for
// the viewport to settle (max 500ms) and snap once.
function FlyToPoint({ point, onComplete }) {
  const map = useMap();
  useEffect(() => {
    if (!point || point.length !== 2 || isNaN(point[0]) || isNaN(point[1])) return;
    if (window.innerWidth > 800) {
      setViewVisibleCenter(map, point, 15);
      if (onComplete) onComplete();
      return;
    }
    let done = false;
    let settleTimer = null;
    const vv = window.visualViewport;
    const finish = () => {
      if (done) return;
      done = true;
      if (vv) vv.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(maxTimer);
      setViewVisibleCenter(map, point, 15);
      if (onComplete) onComplete();
    };
    const onResize = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(finish, 120);
    };
    if (vv) vv.addEventListener('resize', onResize);
    settleTimer = setTimeout(finish, 150);
    const maxTimer = setTimeout(finish, 500);
    return () => {
      done = true;
      if (vv) vv.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(maxTimer);
    };
  }, [point, map]);
  return null;
}

// Official Franken Lagen map (Regierung von Unterfranken) as an image base
// layer. In adjust mode, dragging the map translates the overlay instead of
// panning, for fine-tuning the georeference; the result is reported up and
// persisted.
function LagenOverlay({ bounds, adjusting, onAdjusted }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    const layer = L.imageOverlay('/franken-lagen.png', L.latLngBounds(bounds), {
      pane: 'lagen',
    });
    layer.addTo(map);
    layerRef.current = layer;
    return () => { map.removeLayer(layer); layerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // External bounds updates (e.g. loaded from storage)
  useEffect(() => {
    layerRef.current?.setBounds(L.latLngBounds(bounds));
  }, [bounds]);

  useEffect(() => {
    if (!adjusting) return;
    map.dragging.disable();
    const el = map.getContainer();
    el.classList.add('lagen-adjusting');
    let start = null, startBounds = null;
    const down = (e) => {
      start = { x: e.clientX, y: e.clientY };
      startBounds = layerRef.current.getBounds();
    };
    const move = (e) => {
      if (!start || !layerRef.current) return;
      const dx = e.clientX - start.x, dy = e.clientY - start.y;
      const swPt = map.latLngToContainerPoint(startBounds.getSouthWest()).add([dx, dy]);
      const nePt = map.latLngToContainerPoint(startBounds.getNorthEast()).add([dx, dy]);
      layerRef.current.setBounds(L.latLngBounds(
        map.containerPointToLatLng(swPt), map.containerPointToLatLng(nePt)
      ));
    };
    const up = () => {
      if (!start || !layerRef.current) { start = null; return; }
      start = null;
      const bb = layerRef.current.getBounds();
      onAdjusted([[bb.getSouth(), bb.getWest()], [bb.getNorth(), bb.getEast()]]);
    };
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      map.dragging.enable();
      el.classList.remove('lagen-adjusting');
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [adjusting, map, onAdjusted]);

  return null;
}

// Temporary single-vineyard polygon shown while full region data loads
function PreviewLayer({ feature }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (!feature || !feature.geometry) return;
    try {
      const notable = !!feature.properties?.notable;
      const layer = L.geoJSON(feature, {
        pane: 'vineyards-pane',
        style: { fillColor: notable ? NOTABLE_GOLD : '#6d5aad', fillOpacity: 0.7, color: notable ? NOTABLE_GOLD : '#6d5aad', weight: 1.5 },
        interactive: false
      });
      layer.addTo(map);
      layerRef.current = layer;
    } catch (e) {
      console.warn('[PreviewLayer]', e.message);
    }
    return () => { if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; } };
  }, [feature, map]);

  return null;
}

function InvalidateOnTheme() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 100);
  }, [map]);
  return null;
}

function FitToGermany({ centered }) {
  const map = useMap();
  useEffect(() => {
    map.setMinZoom(5);
    // NOTE: no setMaxZoom here — it silently overrides the MapContainer/
    // tile-layer maxZoom props (this line capped the app at 16 through two
    // rounds of "raise the zoom" that appeared to do nothing).
    // Mobile: the bottom sheet's fit padding (padOpts) wants the view
    // center well SOUTH of Germany at overview zooms so the country sits
    // centered in the visible strip above the sheet — the desktop 42°N
    // south edge clamps that center back and re-centers Germany
    // mid-screen instead. The mask hides the extra pannable area.
    // Desktop lon span matters at far zooms: when the viewport is wider
    // (in degrees) than the box, Leaflet pins the center to the box
    // center and panning bounces. The old 26° span did that on mid-width
    // screens through z6, forcing Germany screen-centered — i.e. partly
    // under the right sidebar with no way to pan it left. 39°, weighted
    // east, un-pins panning a zoom earlier and puts the clamped center
    // east of Germany so it rests left of the sidebar when pinned.
    map.setMaxBounds(window.innerWidth <= 800 ? ATLAS.maxBounds.mobile : ATLAS.maxBounds.desktop);
    map.invalidateSize();
    // Snap, don't slide: this runs one frame after mount, and animating
    // from the initial center/zoom reads as the whole map sliding in.
    // Study Mode: no sidebar, so drop the right-hand fit padding and
    // center Germany symmetrically in the viewport.
    map.fitBounds(GERMANY_BOUNDS, centered ? { padding: [30, 30], animate: false } : padOpts(20, { animate: false }));
  }, [map, centered]);
  return null;
}

// Snap back to the default overview frame on demand (geo search results
// land on the overview regardless of where the user was zoomed).
function FitOverviewOnSignal({ signal }) {
  const map = useMap();
  const last = useRef(signal);
  useEffect(() => {
    if (signal === last.current) return;
    last.current = signal;
    map.fitBounds(GERMANY_BOUNDS, padOpts(20));
  }, [signal, map]);
  return null;
}

// Auto-zoom when mode changes (not on data refetch within the same mode).
// Tracks both mode and dataVersion: only fits when mode changes AND matching
// data has arrived (correct dataVersion). Ignores same-mode refetches.
function FitToData({ data, mode, dataVersion, skip, previewBounds }) {
  const map = useMap();
  const fittedMode = useRef(null);
  const lastSeenVersion = useRef(dataVersion);

  useEffect(() => {
    const modeKey = `${mode.kind}-${mode.name ?? ''}`;

    // Track latest version we've seen, but only fit on mode transitions
    if (modeKey === fittedMode.current) {
      lastSeenVersion.current = dataVersion;
      return;
    }

    // Skip region zoom when a search result will handle its own zoom
    if (skip) {
      fittedMode.current = modeKey;
      lastSeenVersion.current = dataVersion;
      return;
    }

    // Instant zoom: the preview index knows the region's exact bbox before
    // any data has arrived — don't wait for the network.
    if (mode.kind === 'region' && previewBounds) {
      fittedMode.current = modeKey;
      lastSeenVersion.current = dataVersion;
      map.fitBounds(previewBounds, padOpts(30));
      return;
    }

    if (!data) return;

    // For region mode: zoom immediately using the region polygon from stale regions data
    // instead of waiting for vineyard data to arrive
    if (mode.kind === 'region' && data.region !== mode.name) {
      // data is still the regions FeatureCollection — find the matching region polygon
      if (data.type === 'FeatureCollection' && data.features) {
        const regionFeature = data.features.find(f => f.properties?.name === mode.name && f.geometry);
        if (regionFeature) {
          fittedMode.current = modeKey;
          lastSeenVersion.current = dataVersion;
          const geoLayer = L.geoJSON(regionFeature);
          const bounds = geoLayer.getBounds();
          if (bounds.isValid()) {
            map.fitBounds(bounds, padOpts(30));
          }
        }
      }
      return;
    }

    fittedMode.current = modeKey;
    lastSeenVersion.current = dataVersion;

    if (mode.kind === 'regions' || mode.kind === 'unassigned') {
      map.fitBounds(GERMANY_BOUNDS, padOpts(20));
      return;
    }

    if (!data.features || data.features.length === 0) return;
    const geoLayer = L.geoJSON(data);
    const bounds = geoLayer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, padOpts(30));
    }
  }, [data, mode, map, dataVersion, previewBounds]);

  return null;
}

// Zoom to selected vineyard polygon (only when triggered from sidebar)
function FitToSelection({ data, selectedId, zoomToSelection }) {
  const map = useMap();
  const prevId = useRef(null);

  useEffect(() => {
    if (!selectedId || selectedId === prevId.current || !zoomToSelection) {
      return;
    }
    prevId.current = selectedId;

    if (!data || !data.features) { return; }
    const feature = data.features.find(f => f.properties.id === selectedId);
    if (!feature || !feature.geometry) { return; }

    const geoLayer = L.geoJSON(feature);
    const bounds = geoLayer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, padOpts(80, { maxZoom: 16, animate: false }));
    }
  }, [data, selectedId, map]);

  // Clear prev when deselected so re-selecting the same one works
  useEffect(() => {
    if (!selectedId) prevId.current = null;
  }, [selectedId]);

  return null;
}

// Zoom to a selected village
function FitToVillage({ village, zoomToSelection }) {
  const map = useMap();
  const prevId = useRef(null);

  useEffect(() => {
    if (!village || village.id === prevId.current || !zoomToSelection) return;
    prevId.current = village.id;
    if (village.latitude && village.longitude && !isNaN(village.latitude) && !isNaN(village.longitude)) {
      setViewVisibleCenter(map, [village.latitude, village.longitude], 15);
    }
  }, [village, zoomToSelection, map]);

  useEffect(() => {
    if (!village) prevId.current = null;
  }, [village]);

  return null;
}

// Zoom to a specific bereich's features, or back to full region.
// prevKey starts as '__all__' so it doesn't fire on initial mount with stale data.
function FitToBereich({ data, mode, focusBereich, selectedId, selectedVillage, selectedProducer }) {
  const map = useMap();
  const prevKey = useRef('__all__');

  useEffect(() => {
    // Skip bereich-level zoom if a vineyard/village/producer is selected —
    // their own zoom takes priority (still record the key so a later manual
    // focus change refits correctly).
    if (selectedId || selectedVillage || selectedProducer) {
      prevKey.current = focusBereich ?? '__all__';
      return;
    }

    const key = focusBereich ?? '__all__';
    if (key === prevKey.current) return;
    prevKey.current = key;

    if (!data || !data.features) return;
    // Never fit with another region's stale data: during a cross-region
    // switch the focus reset would otherwise refit to the OLD region and
    // stomp a search fly-to already framing the destination.
    if (mode?.kind === 'region' && data.region !== mode.name) return;

    if (!focusBereich) {
      // Zoom back to full region
      const geoLayer = L.geoJSON(data);
      const bounds = geoLayer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, padOpts(30, { animate: false }));
      return;
    }

    const filtered = data.features.filter(f => f.properties.bereich === focusBereich);
    if (filtered.length === 0) return;
    const geoLayer = L.geoJSON({ type: 'FeatureCollection', features: filtered });
    const bounds = geoLayer.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, padOpts(30, { animate: false }));
  }, [data, focusBereich, map, selectedVillage, selectedProducer]);

  return null;
}

// Manual label position overrides for regions with outlier geometry
const LABEL_OVERRIDES = {
  'Saale-Unstrut': [51.25, 11.75],
  'Baden': [48.1, 7.85]
};

// Long names break into stacked lines with near-zero line spacing. Split at
// the first slash or hyphen (separator stays on line 1) or, failing those,
// at the first space.
function stackedLabelLines(name) {
  for (const sep of ['/', '-']) {
    const i = name.indexOf(sep);
    if (i > 0 && i < name.length - 1) return [name.slice(0, i + 1), name.slice(i + 1)];
  }
  const i = name.indexOf(' ');
  if (i > 0) return [name.slice(0, i), name.slice(i + 1)];
  return [name];
}
const REGION_LABEL_LINES = ATLAS.regionLabelLines;
const REGION_LABEL_SCALE = ATLAS.regionLabelScale;
const REGION_LABEL_ANCHOR = ATLAS.regionLabelAnchor ?? {};
const stackedLabelHtml = (lines, color, fontSize, anchor) => {
  const fs = fontSize ? `;font-size:${fontSize}px` : '';
  const tr = anchor === 'bottom-right' ? ';transform:translate(-100%,-100%)' : '';
  return `<span style="color:${color};display:inline-block;line-height:1.02${fs}${tr}">${lines.map(escapeHtml).join('<br>')}</span>`;
};
const regionLabelHtml = (name, color, fontSize) =>
  stackedLabelHtml(REGION_LABEL_LINES[name] ?? [name], color, fontSize, REGION_LABEL_ANCHOR[name]);
const bereichLabelHtml = (name, color, fontSize) =>
  stackedLabelHtml(stackedLabelLines(name), color, fontSize);

// Wine Route overlay (trip planner): dashed line through the planned
// stops with numbered badges riding above the producer glasses, plus a
// car badge on the starting town. Fits the map to the whole loop.
const TRIP_CAR_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 11 6.6 6.8A2 2 0 0 1 8.5 5.5h7a2 2 0 0 1 1.9 1.3L19 11"/><path d="M4 11h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1"/><path d="M3 12v4a1 1 0 0 0 1 1h1"/><circle cx="7.5" cy="16.5" r="1.7"/><circle cx="16.5" cy="16.5" r="1.7"/></svg>';
function TripRouteOverlay({ plan }) {
  const map = useMap();
  useEffect(() => {
    if (!plan?.route?.length) return undefined;
    if (!map.getPane('trip')) map.createPane('trip').style.zIndex = 470;
    const group = L.layerGroup();
    const pts = [[plan.start.lat, plan.start.lng],
      ...plan.route.map((p) => [Number(p.latitude), Number(p.longitude)])];
    // real driving geometry (route cache / Mapbox) draws solid; the
    // straight-leg fallback stays dashed to read as approximate
    const line = plan.geometry?.length
      ? L.polyline(plan.geometry, { pane: 'trip', color: '#6d5aad', weight: 3, opacity: 0.9, interactive: false })
      : L.polyline(pts, { pane: 'trip', color: '#6d5aad', weight: 2.5, dashArray: '6 6', opacity: 0.85, interactive: false });
    group.addLayer(line);
    group.addLayer(L.marker(pts[0], {
      pane: 'trip', interactive: false,
      icon: L.divIcon({ className: 'trip-stop-badge trip-stop-badge--start', html: TRIP_CAR_SVG, iconSize: [22, 22], iconAnchor: [11, 11] }),
    }));
    plan.route.forEach((p, i) => {
      group.addLayer(L.marker([Number(p.latitude), Number(p.longitude)], {
        pane: 'trip', interactive: false,
        icon: L.divIcon({ className: 'trip-stop-badge', html: String(i + 1), iconSize: [19, 19], iconAnchor: [9.5, 26] }),
      }));
    });
    group.addTo(map);
    map.fitBounds(plan.geometry?.length ? L.latLngBounds(plan.geometry) : L.latLngBounds(pts), padOpts(40));
    return () => { map.removeLayer(group); };
  }, [map, plan]);
  return null;
}

// Clickable lakes (atlas config; michigan's Lake Michigan): a transparent
// interactive polygon over the basemap water opening the geo panel. The
// fill stays invisible until hover, and the polygon sits UNDER vineyards
// so shoreline sites keep click priority.
function ClickableLakes({ onSelect }) {
  const map = useMap();
  useEffect(() => {
    if (!ATLAS.clickableLakes?.length) return;
    const layers = [];
    let cancel = false;
    for (const lake of ATLAS.clickableLakes) {
      fetch(lake.url).then((r) => r.json()).then((geometry) => {
        if (cancel) return;
        const layer = L.geoJSON({ type: 'Feature', geometry }, {
          pane: 'lakes',
          style: { stroke: false, fill: true, fillColor: '#4d94c4', fillOpacity: 0 },
        });
        layer.bindTooltip(lake.name, { sticky: true, className: 'map-tip', direction: 'top', offset: [0, -6] });
        layer.on('click', () => onSelect({ kind: 'lake', name: lake.name }));
        layer.addTo(map);
        layers.push(layer);
      }).catch(() => {});
    }
    return () => { cancel = true; layers.forEach((l) => l.remove()); };
  }, [map, onSelect]);
  return null;
}

// Region boundary outline inside the region view (atlas feature; on for
// Michigan, off for Germany): a dashed line so the user knows where the
// AVA runs. The line itself carries a hover tooltip naming the region;
// deliberately NO large map label here.
function RegionOutline({ region }) {
  const map = useMap();
  const [geoms, setGeoms] = useState(null); // [{name, geometry}]
  useEffect(() => {
    let cancel = false;
    setGeoms(null);
    // the region's own boundary plus any regions nested inside it
    // (michigan: Fennville rides the Lake Michigan Shore view)
    const wanted = [region, ...(ATLAS.nestedRegions?.[region] ?? [])];
    supabase.rpc('regions_for_map').then(({ data }) => {
      if (cancel) return;
      const found = wanted
        .map((name) => ({ name, geometry: data?.features?.find((ft) => ft.properties?.name === name)?.geometry }))
        .filter((g) => g.geometry);
      if (found.length) setGeoms(found);
    });
    return () => { cancel = true; };
  }, [region]);
  useEffect(() => {
    if (!geoms) return;
    const layers = geoms.map(({ name, geometry }) => {
      const layer = L.geoJSON({ type: 'Feature', geometry }, {
        pane: 'region-outline',
        // fill:false keeps the interior non-interactive — the tooltip only
        // answers on the boundary line itself
        style: { color: colorForRegion(name), weight: 2, opacity: 0.45, fill: false, className: 'region-outline-line' },
      });
      layer.bindTooltip(`${name} ${regionTerm()}`, { sticky: true, className: 'map-tip', direction: 'top', offset: [0, -6] });
      // a click would open the sticky tooltip anchored at the layer's
      // CENTER (visible snap) and the interactive path shows a pointer
      // cursor — the line is informational only, so keep the tooltip at
      // the cursor and swallow the click
      layer.on('click', (e) => { L.DomEvent.stopPropagation(e); layer.openTooltip(e.latlng); });
      layer.addTo(map);
      return layer;
    });
    return () => { layers.forEach((l) => l.remove()); };
  }, [geoms, map]);
  return null;
}

// Region name labels — shown only in regions overview mode. In the Rivers
// & Mountains view (geoView) they render smaller, in one uniform grey,
// not clickable, at their own independently draggable positions
// (regions.geo_label_latitude/longitude).
const GEO_LABEL_GREY = '#8f8a84';

// Geo-view label placements are read LIVE by everyone (~13 tiny rows):
// the baked overview FC only refreshes on deploy, so anonymous users
// would otherwise see stale positions. Module-cached per page load.
let _geoLabelsCache = null;
let _geoLabelsPromise = null;
const fetchGeoRegionLabels = () => {
  _geoLabelsPromise ??= supabase.rpc('geo_region_labels').then(({ data, error }) => {
    _geoLabelsCache = (!error && data && typeof data === 'object') ? data : {};
    return _geoLabelsCache;
  });
  return _geoLabelsPromise;
};
function RegionLabels({ data, onSelectRegion, canEdit, hoveredRegion, onHoverSet, onHoverClear, geoView, geoObstacles }) {
  const map = useMap();
  const layerRef = useRef(null);
  // live geo placements (name -> [lat, lng]); null until the fetch lands
  const [geoPositions, setGeoPositions] = useState(null);
  useEffect(() => {
    if (!geoView) return undefined;
    let alive = true;
    fetchGeoRegionLabels()
      .then((m) => { if (alive) setGeoPositions(m); })
      .catch(() => { if (alive) setGeoPositions({}); });
    return () => { alive = false; };
  }, [geoView]);

  // Hovered region's label keeps its color; the rest grey out. In the geo
  // view the hovered region lights up as a shape instead — its label HIDES
  // (the full-color region is the highlight, the label would clutter it).
  useEffect(() => {
    const group = layerRef.current;
    if (!group) return;
    group.eachLayer(m => {
      if (!m._labelName || !m.getElement) return;
      const el = m.getElement();
      if (!el) return;
      if (geoView) {
        el.style.display = (!!hoveredRegion && m._labelName === hoveredRegion) ? 'none' : '';
      } else {
        el.classList.toggle('region-label--dim', !!hoveredRegion && m._labelName !== hoveredRegion);
      }
    });
  }, [hoveredRegion, geoView]);

  useEffect(() => {
    if (!data || !data.features) return;
    const group = L.layerGroup();
    layerRef.current = group;
    const cls = `region-label${canEdit ? ' region-label--editable' : ''}${geoView ? ' region-label--geo' : ''}`;
    const colorFor = (name) => (geoView ? GEO_LABEL_GREY : colorForRegion(name));

    const markers = data.features
      .filter(f => f.geometry && f.properties.has_geometry)
      .map(f => {
        const name = f.properties.name;
        const c = colorFor(name);
        const p = f.properties;
        const override = LABEL_OVERRIDES[name];
        let center;
        try {
          // stored drag position wins (the geo view has its own, read live
          // so signed-in and anonymous always agree; the FC fields are the
          // fallback while that fetch is in flight), then the static
          // override, then the geometry's bounds center
          const live = geoView ? geoPositions?.[name] : null;
          const [latField, lngField] = geoView && p.geo_label_latitude != null
            ? ['geo_label_latitude', 'geo_label_longitude']
            : ['label_latitude', 'label_longitude'];
          center = live
            ? L.latLng(live[0], live[1])
            : (p[latField] != null && p[lngField] != null)
              ? L.latLng(p[latField], p[lngField])
              : override
                ? L.latLng(override[0], override[1])
                : L.geoJSON(f).getBounds().getCenter();
          if (isNaN(center.lat) || isNaN(center.lng)) return null;
        } catch { return null; }

        const marker = L.marker(center, {
          interactive: geoView ? !!canEdit : true,
          draggable: !!canEdit,
          pane: 'labels',
          icon: L.divIcon({
            className: cls,
            html: regionLabelHtml(name, c),
            iconSize: null
          })
        });
        marker._labelName = name;
        if (!geoView) {
          marker.on('click', () => onSelectRegion(name));
          if (!isMobile()) {
            marker.on('mouseover', () => onHoverSet?.(name));
            marker.on('mouseout', () => onHoverClear?.());
          }
        }
        if (canEdit) {
          let orig = center;
          marker.on('dragend', async () => {
            const pos = marker.getLatLng();
            try {
              const { error } = await supabase.rpc(
                geoView ? 'update_region_geo_label_position' : 'update_region_label_position', {
                  p_name: name, p_latitude: pos.lat, p_longitude: pos.lng
                });
              if (error) throw error;
              // update the in-memory feature too — the layer remounts from
              // `data` when the tool toggles, and stale props would snap the
              // label back to its old spot (DB was already saved)
              p[geoView ? 'geo_label_latitude' : 'label_latitude'] = pos.lat;
              p[geoView ? 'geo_label_longitude' : 'label_longitude'] = pos.lng;
              if (geoView && _geoLabelsCache) _geoLabelsCache[name] = [pos.lat, pos.lng];
              orig = pos;
              const toast = L.marker(pos, {
                interactive: false, pane: 'labels',
                icon: L.divIcon({ className: 'village-toast village-toast--ok', html: '✓ Saved', iconSize: null, iconAnchor: [-10, -10] })
              });
              group.addLayer(toast);
              setTimeout(() => group.removeLayer(toast), 2000);
            } catch (e) {
              marker.setLatLng(orig);
              const toast = L.marker(orig, {
                interactive: false, pane: 'labels',
                icon: L.divIcon({ className: 'village-toast village-toast--err', html: escapeHtml(e.message ?? 'Save failed'), iconSize: null, iconAnchor: [-10, -10] })
              });
              group.addLayer(toast);
              setTimeout(() => group.removeLayer(toast), 3000);
            }
          });
        }
        return marker;
      }).filter(Boolean);

    const updateLabels = () => {
      const zoom = map.getZoom();
      // Scale font size with zoom, 1.5x the original ramp. The geo view
      // pins them at the mountain-label size instead — backdrop text.
      const fontSize = geoView ? 11.5 : Math.round((6 + (zoom - 5) * 3) * 1.5);
      const rects = [];
      // geo view: mountains, rivers and their labels claim their space
      // first — a region label colliding with any of them stays hidden
      // until zooming in resolves it (producers view shares the label
      // STYLE but has no geo features, so no obstacle seeding)
      if (geoObstacles) rects.push(...geoObstacleRects(map));

      for (const m of markers) {
        group.removeLayer(m);
        if (!map.getBounds().contains(m.getLatLng())) continue;

        const pt = map.latLngToContainerPoint(m.getLatLng());
        const labelFs = geoView ? fontSize : Math.round(fontSize * (REGION_LABEL_SCALE[m._labelName] ?? 1));
        const w = m._labelName.length * labelFs * 0.55;
        const h = labelFs * 1.4;
        const rect = REGION_LABEL_ANCHOR[m._labelName] === 'bottom-right'
          ? { x: pt.x - w, y: pt.y - h, w, h }
          : { x: pt.x - w / 2, y: pt.y - h / 2, w, h };

        // Check overlap with placed labels (edit mode shows everything —
        // hiding a label would make it impossible to reposition)
        const overlaps = !canEdit && rects.some(r =>
          rect.x < r.x + r.w && rect.x + rect.w > r.x &&
          rect.y < r.y + r.h && rect.y + rect.h > r.y
        );
        if (overlaps) continue;

        rects.push(rect);
        m.getElement?.()?.style?.setProperty('font-size', labelFs + 'px');
        // Update icon to set font size
        m.setIcon(L.divIcon({
          className: cls,
          html: regionLabelHtml(m._labelName, colorFor(m._labelName), labelFs),
          iconSize: null
        }));
        group.addLayer(m);
      }
    };

    group.addTo(map);
    updateLabels();
    map.on('zoomend moveend', updateLabels);
    // the geo layer's river labels are DB-backed and mount after us —
    // re-place when its obstacle set changes
    if (geoObstacles) map.on('geo-obstacles', updateLabels);

    return () => {
      map.off('zoomend moveend', updateLabels);
      if (geoObstacles) map.off('geo-obstacles', updateLabels);
      map.removeLayer(group);
    };
  }, [data, map, canEdit, geoView, geoObstacles, geoPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// Bereich name labels — shown when viewing a region but no specific bereich is focused
function BereichLabels({ data, anbaugebiet, onFocusBereich }) {
  const map = useMap();

  useEffect(() => {
    if (!data || !data.features) return;

    // Group features by bereich, compute centroid per group
    const groups = {};
    for (const f of data.features) {
      const b = f.properties.bereich;

      if (!b) continue;
      if (!groups[b]) groups[b] = [];
      groups[b].push(f);
    }

    // Don't show labels if only one bereich
    if (Object.keys(groups).length <= 1) return;

    const group = L.layerGroup();
    const markers = Object.entries(groups).map(([name, features]) => {
      const c = colorForBereich(name, anbaugebiet);
      let center;
      try {
        const geoLayer = L.geoJSON({ type: 'FeatureCollection', features });
        center = geoLayer.getBounds().getCenter();
        if (isNaN(center.lat) || isNaN(center.lng)) return null;
      } catch { return null; }

      const marker = L.marker(center, {
        interactive: true,
        pane: 'labels',
        icon: L.divIcon({
          className: 'region-label',
          html: bereichLabelHtml(name, c),
          iconSize: null
        })
      });
      marker._labelName = name;
      marker.on('click', () => onFocusBereich(name));
      return marker;
    }).filter(Boolean);

    const updateLabels = () => {
      const zoom = map.getZoom();
      const fontSize = Math.round(12 + (zoom - 8) * 2);
      if (fontSize < 6) { markers.forEach(m => group.removeLayer(m)); return; }
      const rects = [];

      for (const m of markers) {
        group.removeLayer(m);
        if (!map.getBounds().contains(m.getLatLng())) continue;

        const pt = map.latLngToContainerPoint(m.getLatLng());
        const lines = stackedLabelLines(m._labelName);
        const w = Math.max(...lines.map(l => l.length)) * fontSize * 0.55;
        const h = fontSize * 1.4 * lines.length;
        const rect = { x: pt.x - w / 2, y: pt.y - h / 2, w, h };

        const overlaps = rects.some(r =>
          rect.x < r.x + r.w && rect.x + rect.w > r.x &&
          rect.y < r.y + r.h && rect.y + rect.h > r.y
        );
        if (overlaps) continue;

        rects.push(rect);
        m.setIcon(L.divIcon({
          className: 'region-label',
          html: bereichLabelHtml(m._labelName, colorForBereich(m._labelName, anbaugebiet), fontSize),
          iconSize: null
        }));
        group.addLayer(m);
      }
    };

    group.addTo(map);
    updateLabels();
    map.on('zoomend moveend', updateLabels);

    return () => {
      map.off('zoomend moveend', updateLabels);
      map.removeLayer(group);
    };
  }, [data, anbaugebiet, map]);

  return null;
}

// VDP GG but not GWS/notable: shown as a plain black polygon with no label.
const isVdpGgBlack = (p) => !!p.vdp_gg_eligible && !p.notable;

// Notable vineyard labels — DivIcon markers with optional drag-to-reposition
function NotableLabels({ data, focusBereich, canEdit, onSelectVineyard, hoveredId }) {
  const map = useMap();

  useEffect(() => {
    if (!data || !data.features) return;

    const notables = data.features.filter(f => {
      const p = f.properties;
      if (!p.notable && !p.vdp_gg_eligible && !p.official_grosses_gewaechs_eligible) return false;
      if (!(p.canonical_name_de || p.einzellage)) return false;
      if (focusBereich && p.bereich !== focusBereich) return false;
      return true;
    });

    if (notables.length === 0) return;

    const group = L.layerGroup();
    const allMarkers = [];

    for (const f of notables) {
      const p = f.properties;
      const name = p.canonical_name_de || p.einzellage;

      // Use stored label position, fall back to polygon centroid
      let center;
      if (p.label_latitude != null && p.label_longitude != null) {
        if (isNaN(p.label_latitude) || isNaN(p.label_longitude)) continue;
        center = L.latLng(p.label_latitude, p.label_longitude);
      } else {
        try {
          if (!f.geometry || !f.geometry.coordinates) continue;
          const gj = L.geoJSON(f);
          const bounds = gj.getBounds();
          if (!bounds.isValid()) continue;
          center = bounds.getCenter();
          if (isNaN(center.lat) || isNaN(center.lng)) continue;
        } catch {
          continue;
        }
      }

      const marker = L.marker(center, {
        draggable: !!canEdit,
        interactive: true,
        pane: 'notable-labels',
        icon: L.divIcon({
          className: `notable-label ${canEdit ? 'notable-label--editable' : ''}`,
          html: escapeHtml(name),
          iconSize: null
        })
      });
      marker._labelName = name;
      marker._vineyardId = p.id;
      marker._isNotable = !!p.notable;
      // GG badge (same rule as the sidebar title, minus notable: gold GWS
      // labels stay clean, black VDP-GG sites carry the tag)
      marker._isGG = !p.notable && (!!p.vdp_gg_eligible || !!p.official_grosses_gewaechs_eligible);

      marker.on('click', () => onSelectVineyard(p.id));

      if (canEdit) {
        const origLatLng = center;

        marker.on('dragend', async () => {
          const pos = marker.getLatLng();
          try {
            const { error } = await supabase.rpc('update_vineyard_label_position', {
              p_id: p.id, p_latitude: pos.lat, p_longitude: pos.lng
            });
            if (error) throw error;

            // Update properties so re-render uses new position
            p.label_latitude = pos.lat;
            p.label_longitude = pos.lng;

            const toast = L.marker(pos, {
              interactive: false,
              pane: 'notable-labels',
              icon: L.divIcon({
                className: 'village-toast village-toast--ok',
                html: '✓ Saved',
                iconSize: null,
                iconAnchor: [-10, -10]
              })
            });
            group.addLayer(toast);
            setTimeout(() => group.removeLayer(toast), 2000);
          } catch (e) {
            marker.setLatLng(origLatLng);
            const toast = L.marker(origLatLng, {
              interactive: false,
              pane: 'notable-labels',
              icon: L.divIcon({
                className: 'village-toast village-toast--err',
                html: escapeHtml(e.message ?? 'Save failed'),
                iconSize: null,
                iconAnchor: [-10, -10]
              })
            });
            group.addLayer(toast);
            setTimeout(() => group.removeLayer(toast), 3000);
          }
        });
      }

      group.addLayer(marker);
      allMarkers.push(marker);
    }

    // Collision resolution is greedy (first-placed wins), so process GWS
    // (notable) labels first — a GWS label always beats a non-GWS one it
    // overlaps. A sidebar-hovered vineyard outranks everything: its label
    // temporarily wins any deconfliction it was losing.
    const rank = (m) => (m._vineyardId === hoveredId ? 2 : m._isNotable ? 1 : 0);
    allMarkers.sort((a, b) => rank(b) - rank(a));

    const ggTag = '<span class="notable-label__gg">GG</span>';
    const labelHtml = (m, fontSize) =>
      `<span style="font-size:${fontSize}px">${escapeHtml(m._labelName)}${m._isGG ? ggTag : ''}</span>`;

    const updateVisibility = () => {
      const zoom = map.getZoom();
      // Key gold/GG labels run ~60% larger than the base ramp at every zoom;
      // below the layer's zoom cutoff the ramp clamps to its floor (used by
      // the hovered-only label)
      const fontSize = Math.round((7 + (Math.max(zoom, 12) - 12) * 1.5) * 1.60);
      if (zoom < 12) {
        allMarkers.forEach(m => group.removeLayer(m));
        // sidebar hover: show just that one label, same style, ramp floor
        const hm = hoveredId ? allMarkers.find(m => m._vineyardId === hoveredId) : null;
        if (hm && map.getBounds().contains(hm.getLatLng())) {
          hm.setIcon(L.divIcon({
            className: `notable-label ${canEdit ? 'notable-label--editable' : ''}`,
            html: labelHtml(hm, fontSize),
            iconSize: null
          }));
          group.addLayer(hm);
        }
        return;
      }
      const skipCollision = zoom >= 17;
      const rects = [];

      for (const m of allMarkers) {
        group.removeLayer(m);
        if (!map.getBounds().contains(m.getLatLng())) continue;

        if (!skipCollision) {
          const pt = map.latLngToContainerPoint(m.getLatLng());
          const w = m._labelName.length * fontSize * 0.5 + (m._isGG ? fontSize * 1.6 : 0);
          const h = fontSize * 1.4;
          const rect = { x: pt.x - w / 2, y: pt.y - h / 2, w, h };

          const overlaps = rects.some(r =>
            rect.x < r.x + r.w && rect.x + rect.w > r.x &&
            rect.y < r.y + r.h && rect.y + rect.h > r.y
          );
          if (overlaps) continue;
          rects.push(rect);
        }
        m.setIcon(L.divIcon({
          className: `notable-label ${canEdit ? 'notable-label--editable' : ''}`,
          html: labelHtml(m, fontSize),
          iconSize: null
        }));
        group.addLayer(m);
      }
    };

    group.addTo(map);
    updateVisibility();
    map.on('zoomend moveend', updateVisibility);

    return () => {
      map.off('zoomend moveend', updateVisibility);
      map.removeLayer(group);
    };
  }, [data, focusBereich, canEdit, map, onSelectVineyard, hoveredId]);

  return null;
}

// House village marker (was an orange dot). Flat SVG so it tints the
// village color and drops a soft shadow to read on the map.
const VILLAGE_GLYPH = '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="#d97706" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>';
const villageIcon = (size, canEdit) => L.divIcon({
  className: `village-glyph ${canEdit ? 'village-glyph--editable' : ''}`,
  html: VILLAGE_GLYPH,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2]
});

// Village markers — shown only in region mode
// Tour helicopter: sits over the region's real airfield, rotor spinning;
// clicking launches that region's fly-through.
function HeliportMarker({ onStart, heliport, label }) {
  const map = useMap();
  useEffect(() => {
    // sized + faded by zoom (heliStyleForZoom): subtle at first appearance,
    // full presence by zoom 14
    const iconFor = (size) => L.divIcon({
      className: 'heli-marker',
      html: `
      <div class="heli-marker__icon" style="width:${size}px;height:${size}px">
        <svg viewBox="0 0 34 34" width="100%" height="100%" aria-hidden="true">
          <rect x="15.4" y="17" width="3.2" height="11" rx="1.4" fill="#374151"/>
          <rect x="12.2" y="26.6" width="9.6" height="1.9" rx="0.95" fill="#374151"/>
          <ellipse cx="17" cy="13.5" rx="5.2" ry="7.2" fill="#4A235A"/>
          <ellipse cx="17" cy="10.6" rx="3.1" ry="3.4" fill="#c4b5fd"/>
        </svg>
        <svg class="heli-marker__rotor" viewBox="0 0 34 34" width="100%" height="100%" aria-hidden="true">
          <g stroke="#1c1c1c" stroke-width="1.6" stroke-linecap="round">
            <line x1="3" y1="13.5" x2="31" y2="13.5"/>
            <line x1="17" y1="-0.5" x2="17" y2="27.5"/>
          </g>
          <circle cx="17" cy="13.5" r="1.7" fill="#1c1c1c"/>
        </svg>
      </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    const first = heliStyleForZoom(map.getZoom());
    const m = L.marker(heliport, {
      pane: 'producers',
      icon: iconFor(first.size),
      opacity: first.opacity,
    }).addTo(map);
    // app-style tooltip instead of a permanent label
    m.bindTooltip(label, {
      direction: 'top', offset: [0, -14], className: 'heli-tooltip', opacity: 1,
    });
    m.on('click', () => onStart?.());
    const onZoom = () => {
      const s = heliStyleForZoom(map.getZoom());
      m.setIcon(iconFor(s.size));
      m.setOpacity(s.opacity);
    };
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); map.removeLayer(m); };
  }, [map, onStart, heliport, label]);
  return null;
}

// Sidebar-hover village dot: temporary, non-interactive, independent of
// the Villages layer toggle.
function HoverVillageDot({ village }) {
  const map = useMap();
  useEffect(() => {
    if (!village?.latitude || !village?.longitude) return;
    const m = L.marker([village.latitude, village.longitude], {
      interactive: false,
      pane: 'villages',
      icon: villageIcon(18, false),
    }).addTo(map);
    // same name label the Villages layer renders next to its dots
    const label = L.marker([village.latitude, village.longitude], {
      interactive: false,
      pane: 'villages',
      icon: L.divIcon({
        className: 'village-label',
        html: escapeHtml(village.name),
        iconSize: null,
        iconAnchor: [-10, 5],
      }),
    }).addTo(map);
    return () => { map.removeLayer(m); map.removeLayer(label); };
  }, [map, village]);
  return null;
}

// Sidebar-hover vineyard name label: always shown while hovered, regardless
// of zoom level or the notable-label layer's own visibility/deconfliction.
function HoverVineyardLabel({ data, vineyardId }) {
  const map = useMap();
  useEffect(() => {
    if (!vineyardId || !data?.features) return;
    const f = data.features.find(x => x.properties?.id === vineyardId);
    if (!f) return;
    const p = f.properties;
    const name = p.canonical_name_de || p.einzellage || p.full_label_name;
    if (!name) return;
    let center;
    if (p.label_latitude != null && p.label_longitude != null
        && !isNaN(p.label_latitude) && !isNaN(p.label_longitude)) {
      center = L.latLng(p.label_latitude, p.label_longitude);
    } else {
      try {
        const b = L.geoJSON(f).getBounds();
        if (!b.isValid()) return;
        center = b.getCenter();
        if (isNaN(center.lat) || isNaN(center.lng)) return;
      } catch { return; }
    }
    const m = L.marker(center, {
      interactive: false,
      pane: 'notable-labels',
      icon: L.divIcon({ className: 'notable-label', html: escapeHtml(name), iconSize: null }),
    }).addTo(map);
    // same font ramp as NotableLabels, clamped to its floor below zoom 12
    const size = () => {
      const fontSize = Math.round((7 + (Math.max(map.getZoom(), 12) - 12) * 1.5) * 1.60);
      m.setIcon(L.divIcon({
        className: 'notable-label',
        html: `<span style="font-size:${fontSize}px">${escapeHtml(name)}</span>`,
        iconSize: null,
      }));
    };
    size();
    map.on('zoomend', size);
    return () => { map.off('zoomend', size); map.removeLayer(m); };
  }, [map, data, vineyardId]);
  return null;
}

function VillageMarkers({ region, onSelectVillage, canEdit }) {
  const map = useMap();

  useEffect(() => {
    let cancel = false;
    const group = L.layerGroup();
    const allMarkers = [];

    supabase.rpc('villages_by_region', { p_region: region }).then(({ data }) => {
      if (cancel || !data || !Array.isArray(data)) return;

      for (const v of data) {
        if (!v.latitude || !v.longitude || isNaN(v.latitude) || isNaN(v.longitude)) continue;

        // Dot marker (L.marker with divIcon so draggable works)
        const marker = L.marker([v.latitude, v.longitude], {
          draggable: !!canEdit,
          pane: 'villages',
          icon: villageIcon(18, canEdit)
        });

        // Name label
        const label = L.marker([v.latitude, v.longitude], {
          interactive: true,
          pane: 'villages',
          icon: L.divIcon({
            className: 'village-label',
            html: escapeHtml(v.name),
            iconSize: null,
            iconAnchor: [-10, 5]
          })
        });

        marker.on('click', () => onSelectVillage(v));
        label.on('click', () => onSelectVillage(v));

        if (canEdit) {
          const origLatLng = L.latLng(v.latitude, v.longitude);

          marker.on('dragend', async () => {
            const pos = marker.getLatLng();
            try {
              const { error } = await supabase.rpc('update_village_position', {
                p_id: v.id, p_latitude: pos.lat, p_longitude: pos.lng
              });
              if (error) throw error;

              // Update label position
              label.setLatLng(pos);
              // Update the village object so panel shows correct coords
              v.latitude = pos.lat;
              v.longitude = pos.lng;

              // Brief save confirmation
              const toast = L.marker(pos, {
                interactive: false,
                pane: 'villages',
                icon: L.divIcon({
                  className: 'village-toast village-toast--ok',
                  html: '✓ Saved',
                  iconSize: null,
                  iconAnchor: [-10, -10]
                })
              });
              group.addLayer(toast);
              setTimeout(() => group.removeLayer(toast), 2000);
            } catch (e) {
              // Snap back on error
              marker.setLatLng(origLatLng);
              label.setLatLng(origLatLng);

              const toast = L.marker(origLatLng, {
                interactive: false,
                pane: 'villages',
                icon: L.divIcon({
                  className: 'village-toast village-toast--err',
                  html: escapeHtml(e.message ?? 'Save failed'),
                  iconSize: null,
                  iconAnchor: [-10, -10]
                })
              });
              group.addLayer(toast);
              setTimeout(() => group.removeLayer(toast), 3000);
            }
          });

          // Move label during drag
          marker.on('drag', () => label.setLatLng(marker.getLatLng()));
        }

        group.addLayer(marker);
        group.addLayer(label);
        allMarkers.push({ marker, label, name: v.name });
      }

      const updateSizes = () => {
        const zoom = map.getZoom();
        const glyphSize = Math.min(Math.round(16 + (zoom - 8) * 2.5), 26);
        const fontSize = Math.min(Math.round((9 + (zoom - 8) * 1.5) * 0.85), 11);
        const offset = Math.round(glyphSize / 2 + 4);
        for (const { marker, label, name } of allMarkers) {
          marker.setIcon(villageIcon(glyphSize, canEdit));
          label.setIcon(L.divIcon({
            className: 'village-label',
            html: `<span style="font-size:${fontSize}px">${escapeHtml(name)}</span>`,
            iconSize: null,
            iconAnchor: [-offset, fontSize / 2]
          }));
        }
      };

      group.addTo(map);
      updateSizes();
      map.on('zoomend', updateSizes);
      group._updateSizes = updateSizes;
    });

    return () => {
      cancel = true;
      if (group._updateSizes) map.off('zoomend', group._updateSizes);
      map.removeLayer(group);
    };
  }, [region, map, onSelectVillage, canEdit]);

  return null;
}

// Wine-glass producer marker (was a purple dot). Flat SVG so it tints the
// producer color and drops a soft shadow to read on the map.
const PRODUCER_GLYPH = '<svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true"><path d="M10.6 1.6h2.8v4.6c0 .9.5 1.4 1.3 2 .9.7 1.4 1.6 1.4 2.9v9.2c0 .7-.5 1.2-1.2 1.2H9.1c-.7 0-1.2-.5-1.2-1.2v-9.2c0-1.3.5-2.2 1.4-2.9.8-.6 1.3-1.1 1.3-2V1.6z" fill="#55803e" stroke="#31303e" stroke-width="1.3" stroke-linejoin="round"/><rect x="10.6" y="1.9" width="2.8" height="2.6" fill="#3c5b2c"/><rect x="8.9" y="12.6" width="6.2" height="5.4" rx="0.8" fill="#f2ead6" stroke="#31303e" stroke-width="1"/></svg>';
const producerIcon = (size, muted = false) => L.divIcon({
  className: `producer-glyph${muted ? ' producer-glyph--muted' : ''}`,
  html: PRODUCER_GLYPH,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2]
});

// Key Producers layer (overview): every producer nationwide as a wine-glass
// marker. No permanent labels — the app-style tooltip names them on hover;
// names live in the sidebar's per-region lists.
// Atlas-defined label priority: when producer labels collide, the earlier
// name in ATLAS.producerLabelPriority claims the spot. Prefix matching both
// ways tolerates suffix drift ("Tabor Hill" vs "Tabor Hill Winery").
// Atlases without a list (germany) rank everything equal — order unchanged.
const LABEL_PRIORITY = (ATLAS.producerLabelPriority ?? []).map((n) => n.toLowerCase());
const labelRank = (name) => {
  const n = (name || '').toLowerCase();
  if (!n) return LABEL_PRIORITY.length;
  const i = LABEL_PRIORITY.findIndex((q) => n === q || n.startsWith(q) || q.startsWith(n));
  return i === -1 ? LABEL_PRIORITY.length : i;
};

function KeyProducerMarkers({ producers, onSelect, hoveredId, filterIds }) {
  const map = useMap();
  const byIdRef = useRef(new Map());
  const updateLabelsRef = useRef(null);
  // labels read hover/filter through a ref so the zoom listener never
  // needs re-binding
  const stateRef = useRef({ hoveredId, filterIds });
  stateRef.current = { hoveredId, filterIds };

  useEffect(() => {
    if (!producers?.length) return;
    const group = L.layerGroup();
    const byId = new Map();
    const entries = [];
    for (const p of producers) {
      const lat = Number(p.latitude), lng = Number(p.longitude);
      if (isNaN(lat) || isNaN(lng)) continue;
      const m = L.marker([lat, lng], { interactive: true, pane: 'producers', icon: producerIcon(21) });
      m._kpState = '16-false';
      m.bindTooltip(escapeHtml(p.name), {
        direction: 'top', offset: [0, -10], className: 'heli-tooltip', opacity: 1,
      });
      m.on('click', () => onSelect(p));
      group.addLayer(m);
      byId.set(p.id, m);

      const name = p.short_name || p.name;
      const label = L.marker([lat, lng], {
        interactive: true, pane: 'producers',
        icon: L.divIcon({ className: 'producer-label', html: escapeHtml(name), iconSize: null, iconAnchor: [-10, 5] }),
      });
      label.on('click', () => onSelect(p));
      entries.push({ p, name, label, latlng: L.latLng(lat, lng) });
    }
    byIdRef.current = byId;

    // Producer names appear once zoomed in a few levels, same look and
    // zoom-sizing as the region-view producer labels — but strictly
    // collision-free: hovered wins its spot, then filter matches, then
    // the rest in list order; anything that would overlap stays hidden.
    const LABEL_MIN_ZOOM = 9;
    const LABEL_MAX_W = 110; // px — long names break to two tight lines
    const hits = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    const updateLabels = () => {
      const zoom = map.getZoom();
      const { hoveredId: hid, filterIds: fids } = stateRef.current;
      const fontSize = Math.min(Math.round((9 + (zoom - 8) * 1.5) * 0.85), 11);
      const glyphSize = hid || fids ? 22 : 16;
      const offset = Math.round(glyphSize / 2 + 4);
      const rects = [];
      const score = (e) => (e.p.id === hid ? 2 : fids?.has(e.p.id) ? 1 : 0);
      const ordered = [...entries].sort((a, b) => score(b) - score(a) || labelRank(a.name) - labelRank(b.name));
      for (const e of ordered) {
        const dimmed = !!fids && !fids.has(e.p.id) && e.p.id !== hid;
        let visible = zoom >= LABEL_MIN_ZOOM && !dimmed;
        if (visible) {
          const pt = map.latLngToContainerPoint(e.latlng);
          const textW = e.name.length * fontSize * 0.62;
          const lines = Math.min(2, Math.ceil(textW / LABEL_MAX_W));
          const w = Math.min(textW, LABEL_MAX_W);
          const h = lines * fontSize * 1.0;
          const rect = { x: pt.x + offset, y: pt.y - h / 2, w, h };
          if (rects.some((r) => hits(rect, r))) visible = false;
          else {
            rects.push(rect);
            e.label.setIcon(L.divIcon({
              className: 'producer-label',
              html: `<span style="font-size:${fontSize}px">${escapeHtml(e.name)}</span>`,
              iconSize: null,
              iconAnchor: [-offset, h / 2],
            }));
          }
        }
        if (visible) group.addLayer(e.label);
        else group.removeLayer(e.label);

        // the hover tooltip covers exactly the producers whose label is
        // NOT on the map (zoomed out, collided or filter-muted) — a name
        // already shown next to the glass needs no tooltip
        const glass = byId.get(e.p.id);
        if (glass) {
          if (visible && glass.getTooltip()) glass.unbindTooltip();
          else if (!visible && !glass.getTooltip()) {
            glass.bindTooltip(escapeHtml(e.p.name), {
              direction: 'top', offset: [0, -10], className: 'heli-tooltip', opacity: 1,
            });
          }
        }
      }
    };
    updateLabelsRef.current = updateLabels;
    group.addTo(map);
    updateLabels();
    map.on('zoomend moveend', updateLabels);
    return () => {
      map.off('zoomend moveend', updateLabels);
      updateLabelsRef.current = null;
      byIdRef.current = new Map();
      map.removeLayer(group);
    };
  }, [map, producers, onSelect]);

  // The sidebar filter and row hover reflect on the markers: hovered
  // producer amplifies most; a live filter grows the matches and mutes
  // the rest. Each marker tracks its applied state so a sweep only
  // touches markers that actually change.
  useEffect(() => {
    for (const [id, m] of byIdRef.current) {
      const hovered = id === hoveredId;
      const dim = !!filterIds && !filterIds.has(id) && !hovered;
      const size = hovered ? 36 : filterIds && filterIds.has(id) ? 29 : 21;
      const state = `${size}-${dim}`;
      if (m._kpState === state) continue;
      m._kpState = state;
      m.setIcon(producerIcon(size, dim));
      m.setZIndexOffset(hovered ? 1000 : size === 29 ? 500 : 0);
    }
    updateLabelsRef.current?.();
  }, [hoveredId, filterIds]);
  return null;
}

// Producer markers — shown only in region mode, same visibility as villages
function ProducerMarkers({ region, bereich, onSelectProducer, selectedProducerId }) {
  const map = useMap();
  const selectedRef = useRef(selectedProducerId ?? null);
  const refreshRef = useRef(null);

  // selection changes (open/close the producer sidebar) restyle in place
  useEffect(() => {
    selectedRef.current = selectedProducerId ?? null;
    refreshRef.current?.();
  }, [selectedProducerId]);

  useEffect(() => {
    let cancel = false;
    const group = L.layerGroup();
    const entries = [];
    let hoveredId = null;

    // one region-wide call: the per-bereich fan-out silently dropped
    // producers whose bereich is NULL (every michigan seed row; possible
    // in Germany too) and multiplied RPC round-trips for nothing
    const fetchProducers = supabase
      .rpc('producers_by_bereich', { p_anbaugebiet: region, p_bereich: bereich ?? null })
      .then(r => r.data);

    fetchProducers.then((data) => {
      if (cancel || !data || !Array.isArray(data)) return;

      // Labels are hidden by default in this view: hovering a glass shows
      // its name, clicking pins the name while the sidebar is open and
      // fades the other glasses; backing out hides it again.
      const refresh = () => {
        if (cancel) return;
        const zoom = map.getZoom();
        const glyphSize = Math.min(Math.round(16 + (zoom - 8) * 2.5), 26);
        const fontSize = Math.min(Math.round((9 + (zoom - 8) * 1.5) * 0.85), 11);
        const offset = Math.round(glyphSize / 2 + 4);
        const selId = selectedRef.current;
        for (const e of entries) {
          e.marker.setIcon(producerIcon(glyphSize, selId != null && e.id !== selId));
          if (e.id === hoveredId || e.id === selId) {
            e.label.setIcon(L.divIcon({
              className: 'producer-label',
              html: `<span style="font-size:${fontSize}px">${escapeHtml(e.name)}</span>`,
              iconSize: null,
              iconAnchor: [-offset, fontSize / 2]
            }));
            group.addLayer(e.label);
          } else {
            group.removeLayer(e.label);
          }
        }
      };

      for (const p of data) {
        if (!p.latitude || !p.longitude || isNaN(p.latitude) || isNaN(p.longitude)) continue;

        const displayName = p.short_name || p.name;

        const marker = L.marker([p.latitude, p.longitude], {
          interactive: true,
          pane: 'producers',
          icon: producerIcon(23)
        });

        const label = L.marker([p.latitude, p.longitude], {
          interactive: true,
          pane: 'producers',
          icon: L.divIcon({ className: 'producer-label', html: '', iconSize: null })
        });

        const hoverIn = () => { hoveredId = p.id; refresh(); };
        // grace period so the cursor can travel from the glass onto the
        // label without the label vanishing under it mid-way
        const hoverOut = () => setTimeout(() => {
          if (hoveredId === p.id) { hoveredId = null; refresh(); }
        }, 80);
        marker.on('mouseover', hoverIn);
        marker.on('mouseout', hoverOut);
        label.on('mouseover', hoverIn);
        label.on('mouseout', hoverOut);
        marker.on('click', () => onSelectProducer(p));
        label.on('click', () => onSelectProducer(p));

        group.addLayer(marker);
        entries.push({ marker, label, id: p.id, name: displayName });
      }

      refreshRef.current = refresh;
      group.addTo(map);
      refresh();
      map.on('zoomend', refresh);
      group._refresh = refresh;
    });

    return () => {
      cancel = true;
      if (group._refresh) map.off('zoomend', group._refresh);
      refreshRef.current = null;
      map.removeLayer(group);
    };
  }, [region, bereich, map, onSelectProducer]);

  return null;
}

// Brief loaded count pill
function LoadedPill({ count }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 2500);
    return () => clearTimeout(t);
  }, [count]);
  if (!visible) return null;
  return <div className="map-status">{count.toLocaleString()} vineyards loaded</div>;
}

// ── Viewport culling for in-region vineyard layer ──────────────────────

// Precompute bbox for a GeoJSON feature (returns [south, west, north, east])
function featureBbox(f) {
  const coords = [];
  function walk(c) {
    if (typeof c[0] === 'number') { coords.push(c); return; }
    for (const sub of c) walk(sub);
  }
  if (f.geometry?.coordinates) walk(f.geometry.coordinates);
  if (coords.length === 0) return null;
  let s = Infinity, w = Infinity, n = -Infinity, e = -Infinity;
  for (const [lng, lat] of coords) {
    if (lat < s) s = lat; if (lat > n) n = lat;
    if (lng < w) w = lng; if (lng > e) e = lng;
  }
  return [s, w, n, e];
}

function bboxOverlaps([s1, w1, n1, e1], [s2, w2, n2, e2]) {
  return !(n1 < s2 || s1 > n2 || e1 < w2 || w1 > e2);
}

// Planar shoelace area of a feature's outer ring(s) — only used for relative
// size ordering, so no geodesic correction is needed.
function featureArea(f) {
  const ringArea = (ring) => {
    let sum = 0;
    for (let i = 0, n = ring.length - 1; i < n; i++) {
      sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
    }
    return Math.abs(sum) / 2;
  };
  const g = f.geometry;
  if (!g?.coordinates) return 0;
  if (g.type === 'Polygon') return ringArea(g.coordinates[0] ?? []);
  if (g.type === 'MultiPolygon') {
    let total = 0;
    for (const poly of g.coordinates) total += ringArea(poly[0] ?? []);
    return total;
  }
  return 0;
}


// Expand bounds by a factor (1.5 = 50% bigger on each side)
function expandBounds(bounds, factor) {
  const latSpan = bounds.getNorth() - bounds.getSouth();
  const lngSpan = bounds.getEast() - bounds.getWest();
  const latPad = latSpan * (factor - 1) / 2;
  const lngPad = lngSpan * (factor - 1) / 2;
  return [
    bounds.getSouth() - latPad,
    bounds.getWest() - lngPad,
    bounds.getNorth() + latPad,
    bounds.getEast() + lngPad
  ];
}

function ZoomTracker({ onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => onZoomChange(map.getZoom());
    onZoomChange(map.getZoom());
    map.on('zoomend', handler);
    return () => map.off('zoomend', handler);
  }, [map, onZoomChange]);
  return null;
}

// A drill-down selection can sit beneath a key polygon in the render stack;
// lift it above so the highlight is visible.
function bringSelectedToFront(group, selectedId) {
  if (!selectedId) return;
  group.eachLayer(l => {
    if (l.feature?.properties?.id === selectedId) l.bringToFront();
  });
}

// Renders a culled GeoJSON layer using ref-based clearLayers/addData for efficiency
// ── Custom-vineyard geometry editor ─────────────────────────────────────────
// Every vertex of the session's rings becomes a draggable handle; the ring
// polygon follows live during a drag and state syncs on dragend. Rings that
// self-intersect render red (App computes validity and blocks Save). While
// active, other map layers ignore the pointer (.map--geom-edit), like the
// draw tool's session class.
// Instant-load region placeholder: a pre-rendered PNG of the region's
// vineyards (scripts/render_region_pngs.py) shown while the live GeoJSON
// loads, then faded out. Non-interactive by nature, sits just below the
// vineyard pane, so real polygons draw over it and take clicks as soon as
// they exist. Only for the default view (all vineyards, no bereich focus).
function RegionPreviewOverlay({ info, active, failed }) {
  const map = useMap();
  const [visible, setVisible] = useState(active || failed);
  const [ready, setReady] = useState(false);

  // Pre-decode off-DOM so the overlay's first paint never happens
  // mid-interaction (large images decoding during a zoom read as jank).
  useEffect(() => {
    let on = true;
    const img = new Image();
    img.src = info.url;
    (img.decode ? img.decode() : Promise.resolve())
      .catch(() => {})
      .then(() => { if (on) setReady(true); });
    return () => { on = false; };
  }, [info.url]);

  useEffect(() => {
    if (active || failed) { setVisible(true); return; }
    const t = setTimeout(() => setVisible(false), 450);   // let the fade finish
    return () => clearTimeout(t);
  }, [active, failed]);

  // Fade and grey live on the PANE, never on the <img> Leaflet is
  // transform-animating — styling that element mid-zoom is what made the
  // overlay lag behind the map. No remounts on state change either.
  useEffect(() => {
    const pane = map.getPane('region-preview');
    if (!pane) return;
    pane.classList.toggle('region-preview-pane--hidden', !(active || failed));
    pane.classList.toggle('region-preview-pane--failed', !!failed);
  }, [map, active, failed]);
  useEffect(() => () => {
    map.getPane('region-preview')?.classList.remove('region-preview-pane--hidden', 'region-preview-pane--failed');
  }, [map]);

  if (!visible || !ready) return null;
  return <ImageOverlay url={info.url} bounds={info.bounds} pane="region-preview" />;
}

function GeometryEditLayer({ session, ringValid, onVertexMoved, onVertexInserted, onVerticesDeleted }) {
  const map = useMap();

  useEffect(() => {
    if (!session || !map) return;
    const container = map.getContainer();
    container.classList.add('map--geom-edit');
    // Shift+drag is Leaflet's box-zoom gesture — the same gesture as the
    // vertex lasso. Off for the whole session, restored on exit.
    map.boxZoom.disable();
    const group = L.layerGroup().addTo(map);

    const polys = session.rings.map((ring, pi) => {
      const ok = ringValid[pi] !== false;
      return L.polygon(ring, {
        pane: 'labels',
        color: ok ? '#6d5aad' : '#dc2626',
        weight: 2,
        fillColor: ok ? '#6d5aad' : '#dc2626',
        fillOpacity: 0.08,
        interactive: false,
      }).addTo(group);
    });

    session.rings.forEach((ring, pi) => {
      ring.forEach(([lat, lng], vi) => {
        const m = L.marker([lat, lng], {
          draggable: true,
          pane: 'notable-labels',
          icon: L.divIcon({ className: 'geom-vertex', iconSize: [13, 13] }),
        });
        m.on('drag', () => {
          const latlngs = polys[pi].getLatLngs()[0];
          latlngs[vi] = m.getLatLng();
          polys[pi].setLatLngs(latlngs);
        });
        m.on('dragend', () => {
          const ll = m.getLatLng();
          onVertexMoved(pi, vi, [ll.lat, ll.lng]);
        });
        group.addLayer(m);
      });

      // one add-point handle per segment (including the closing one):
      // clicking inserts a vertex at the segment midpoint, which re-renders
      // the layer with a fresh draggable marker there
      ring.forEach(([lat, lng], vi) => {
        const [lat2, lng2] = ring[(vi + 1) % ring.length];
        const mid = L.marker([(lat + lat2) / 2, (lng + lng2) / 2], {
          pane: 'notable-labels',
          icon: L.divIcon({ className: 'geom-vertex geom-vertex--add', iconSize: [11, 11], html: '+' }),
        });
        mid.on('click', () => {
          const ll = mid.getLatLng();
          onVertexInserted(pi, vi, [ll.lat, ll.lng]);
        });
        group.addLayer(mid);
      });
    });

    // ── Shift-lasso vertex selection + Delete ──
    // Hold Shift and drag to sweep a lasso; enclosed vertices highlight.
    // Delete/Backspace removes them (rings reclose in App state, which
    // rebuilds this whole layer and clears the selection naturally).
    const markers = [];
    group.eachLayer(l => { if (l.options.icon?.options?.className === 'geom-vertex') markers.push(l); });
    // markers were added ring by ring, vertex by vertex — recover keys
    const keys = [];
    session.rings.forEach((ring, pi) => ring.forEach((_, vi) => keys.push([pi, vi])));
    let selected = [];
    const clearSel = () => {
      selected = [];
      markers.forEach(m => m.getElement()?.classList.remove('geom-vertex--selected'));
    };
    let lassoPts = null, lassoLine = null;
    const inPoly = (pt, poly) => {
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i], [xj, yj] = poly[j];
        if (((yi > pt.y) !== (yj > pt.y)) && (pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    };
    const onDown = (e) => {
      if (!e.shiftKey || e.button !== 0) { if (!e.shiftKey) clearSel(); return; }
      L.DomEvent.stop(e);
      map.dragging.disable();
      lassoPts = [];
      const move = (ev) => {
        const cp = map.mouseEventToContainerPoint(ev);
        lassoPts.push([cp.x, cp.y]);
        const lls = lassoPts.map(([x, y]) => map.containerPointToLatLng(L.point(x, y)));
        if (!lassoLine) {
          lassoLine = L.polyline(lls, { pane: 'notable-labels', color: '#6d5aad', weight: 1.5, dashArray: '4 3', fill: true, fillOpacity: 0.06 }).addTo(map);
        } else lassoLine.setLatLngs(lls);
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        map.dragging.enable();
        if (lassoLine) { map.removeLayer(lassoLine); lassoLine = null; }
        if (lassoPts && lassoPts.length >= 3) {
          clearSel();
          markers.forEach((m, i) => {
            const cp = map.latLngToContainerPoint(m.getLatLng());
            if (inPoly(cp, lassoPts)) {
              selected.push(keys[i]);
              m.getElement()?.classList.add('geom-vertex--selected');
            }
          });
        }
        lassoPts = null;
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    };
    const onKey = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected.length > 0) {
        e.preventDefault();
        onVerticesDeleted(selected.slice());
      }
    };
    container.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);

    return () => {
      container.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      if (lassoLine) map.removeLayer(lassoLine);
      map.dragging.enable();
      map.boxZoom.enable();
      container.classList.remove('map--geom-edit');
      map.removeLayer(group);
    };
  }, [session, ringValid, map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function CulledVineyardLayer({ data, selectedId, selectedIds, labelZoomActive, satellite, showAllVineyards, focusBereich, pane, style, onEachFeature, styleSignal }) {
  const map = useMap();
  const layerRef = useRef(null);
  const rebuildRef = useRef(null);
  const styleRef = useRef(style);
  const onEachRef = useRef(onEachFeature);
  const selectedIdRef = useRef(selectedId);
  const selectedIdsRef = useRef(selectedIds);

  // Keep refs current without triggering effects
  styleRef.current = style;
  onEachRef.current = onEachFeature;
  selectedIdRef.current = selectedId;
  selectedIdsRef.current = selectedIds;

  // Precompute bboxes when data changes
  const bboxCache = useMemo(() => {
    if (!data?.features) return new Map();
    const cache = new Map();
    for (const f of data.features) {
      const id = f.properties?.id;
      if (!id) continue;
      const bb = featureBbox(f);
      if (bb) cache.set(id, bb);
    }
    return cache;
  }, [data]);

  // Index features by id for fast lookup
  const featureIndex = useMemo(() => {
    if (!data?.features) return new Map();
    const idx = new Map();
    for (const f of data.features) {
      const id = f.properties?.id;
      if (id) idx.set(id, f);
    }
    return idx;
  }, [data]);

  const computeCulled = useCallback((boundsOverride) => {
    if (!data?.features || !map) return null;
    const bounds = boundsOverride ?? map.getBounds();
    const expanded = expandBounds(bounds, 1.5);
    const culled = [];
    const includedIds = new Set();

    for (const f of data.features) {
      const id = f.properties?.id;
      if (!id) continue;
      const bb = bboxCache.get(id);
      if (!bb) continue;
      if (bboxOverlaps(bb, expanded)) {
        culled.push(f);
        includedIds.add(id);
      }
    }

    // Always include selected vineyard(s) even if off-screen
    const selId = selectedIdRef.current;
    const selIds = selectedIdsRef.current;
    if (selId && !includedIds.has(selId)) {
      const f = featureIndex.get(selId);
      if (f) culled.push(f);
    }
    if (selIds) {
      for (const sid of selIds) {
        if (!includedIds.has(sid)) {
          const f = featureIndex.get(sid);
          if (f) culled.push(f);
        }
      }
    }

    return culled;
  }, [data, map, bboxCache, featureIndex]);

  // Create the Leaflet layer once per data change; diff-update its contents
  // as the viewport moves. Mounted sublayers are tracked in a Map so a cull
  // refresh only touches the polygons that actually enter or leave — the old
  // clearLayers+addData full rebuild flashed every polygon on screen.
  useEffect(() => {
    if (!map || !data?.features) return;

    const layer = L.geoJSON([], {
      pane,
      style: (f) => styleRef.current(f),
      onEachFeature: (f, l) => onEachRef.current(f, l)
    });
    layer.addTo(map);
    layerRef.current = layer;

    const mounted = new Map();
    let mountedIds = null;

    // New sublayers append to the end of the pane regardless of the data
    // sort, so re-assert stacking (data.features is pre-sorted bottom-to-top)
    // after every batch of adds. toFront is an appendChild per path — low
    // single-digit ms at viewport feature counts.
    const restack = () => {
      for (const f of data.features) {
        const l = mounted.get(f.properties?.id);
        if (l) l.bringToFront();
      }
      bringSelectedToFront(layer, selectedIdRef.current);
    };

    // addOnly: during a zoom-in animation the edges are still on screen, so
    // eager refreshes only mount incoming polygons; pruning waits for the
    // settle refresh at moveend/zoomend.
    const applyCull = (boundsOverride, addOnly = false) => {
      const features = computeCulled(boundsOverride);
      if (!features) return;
      const { added, removed, changed } = diffCulled(mountedIds, features);
      if (mountedIds && !changed) return;
      if (!addOnly) {
        for (const id of removed) {
          const l = mounted.get(id);
          if (l) { layer.removeLayer(l); mounted.delete(id); }
        }
      }
      if (added.length > 0) {
        layer.addData({ type: 'FeatureCollection', features: added });
        layer.eachLayer(l => {
          const id = l.feature?.properties?.id;
          if (id && !mounted.has(id)) mounted.set(id, l);
        });
        restack();
      }
      mountedIds = new Set(mounted.keys());
    };

    rebuildRef.current = () => {
      layer.clearLayers();
      mounted.clear();
      mountedIds = null;
      applyCull();
    };

    applyCull();

    // Throttle, not trailing debounce: continuous pan/zoom input refreshes
    // the cull at most every THROTTLE_MS instead of postponing it until the
    // gesture ends (which left viewport-shaped holes while zooming out).
    const THROTTLE_MS = 120;
    let timer = null;
    let lastRun = 0;
    let pendingTarget = null;
    const schedule = (target) => {
      if (target) pendingTarget = target;
      if (timer) return;
      const wait = Math.max(0, THROTTLE_MS - (Date.now() - lastRun));
      timer = setTimeout(() => {
        timer = null;
        lastRun = Date.now();
        const t = pendingTarget;
        pendingTarget = null;
        if (t) applyCull(t, true);
        else applyCull();
      }, wait);
    };

    // zoomanim carries the animation's destination view: cull against the
    // target bounds so polygons for where the view is going mount before the
    // zoom lands. moveend/zoomend always fire after the animation, so a full
    // (pruning) refresh follows every eager one.
    const onZoomAnim = (e) => {
      const size = map.getSize();
      const c = map.project(e.center, e.zoom);
      schedule(L.latLngBounds(
        map.unproject(c.subtract(size.divideBy(2)), e.zoom),
        map.unproject(c.add(size.divideBy(2)), e.zoom)
      ));
    };
    const onViewChange = () => schedule(null);

    map.on('zoomanim', onZoomAnim);
    map.on('move moveend zoomend', onViewChange);

    return () => {
      clearTimeout(timer);
      map.off('zoomanim', onZoomAnim);
      map.off('move moveend zoomend', onViewChange);
      map.removeLayer(layer);
      layerRef.current = null;
      rebuildRef.current = null;
    };
  }, [map, data, pane, computeCulled]);

  // Restyle all visible polygons whenever anything the style function reads
  // changes: selection, the GWS/GG label zoom threshold, the satellite base
  // layer, the GWS & GG Only filter, or an external style signal (e.g. the
  // Flurbereinigung layer's uniform-green override).
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setStyle((f) => styleRef.current(f));
      bringSelectedToFront(layerRef.current, selectedId);
    }
  }, [selectedId, selectedIds, labelZoomActive, satellite, showAllVineyards, styleSignal]);

  // A bereich switch flips thousands of polygons between hidden and visible;
  // in-place setStyle repaints lazily on some engines (polygons read pale
  // until the next zoom rebuilds them), so rebuild the layer contents the
  // same way a zoom does.
  const prevFocusRef = useRef(focusBereich);
  useEffect(() => {
    if (prevFocusRef.current === focusBereich) return;
    prevFocusRef.current = focusBereich;
    rebuildRef.current?.();
  }, [focusBereich, computeCulled]);

  return null;
}

// Overview density dots. At far-out zooms most vineyard polygons are
// sub-pixel and no fill opacity can keep them visible, so below this zoom
// each vineyard also renders as a FIXED-SIZE canvas circle at its label
// point/centroid — graduated symbolization: the map reads as vineyard
// density dots at region scale without touching border weights. Canvas
// renderer keeps thousands of circles cheap; dots are non-interactive so
// clicks still land on the polygons.
const DENSITY_DOTS_MAX_ZOOM = 10;
function DensityDotsLayer({ data, focusBereich, showAllVineyards, mode }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !data?.features?.length) return;
    const renderer = L.canvas({ pane: 'vineyards-pane', padding: 0.3 });
    const group = L.layerGroup();
    for (const f of data.features) {
      const p = f.properties ?? {};
      if (!p.id) continue;
      if (focusBereich && p.bereich !== focusBereich) continue;
      const isKey = isKeyVineyard(p);
      if (!showAllVineyards && !isKey) continue;
      let lat = p.label_latitude, lng = p.label_longitude;
      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
        const bb = featureBbox(f);
        if (!bb) continue;
        lat = (bb[0] + bb[2]) / 2; lng = (bb[1] + bb[3]) / 2;
      }
      const color = p.notable ? NOTABLE_GOLD
        : isVdpGgBlack(p) ? VDP_GG_BLACK
        : mode.kind === 'region' && p.bereich ? colorForBereich(p.bereich, p.anbaugebiet)
        : p.anbaugebiet ? colorForRegion(p.anbaugebiet) : UNCLASSIFIED_COLOR;
      group.addLayer(L.circleMarker([lat, lng], {
        renderer,
        radius: isKey ? 2.2 : 1.6,
        stroke: false,
        fillColor: color,
        fillOpacity: 0.85,
        interactive: false,
      }));
    }
    const sync = () => {
      if (map.getZoom() <= DENSITY_DOTS_MAX_ZOOM) {
        if (!map.hasLayer(group)) group.addTo(map);
      } else if (map.hasLayer(group)) {
        map.removeLayer(group);
        // Drop the renderer's viewport-sized canvas (~35-40MB GPU) too —
        // otherwise it stays mounted at detail zooms where no dot is
        // visible, and every zoom animation re-rasterizes it for nothing.
        // Re-adding the group recreates the container on demand.
        renderer.remove();
      }
    };
    sync();
    map.on('zoomend', sync);
    return () => {
      map.off('zoomend', sync);
      map.removeLayer(group);
      // The explicit canvas renderer is NOT owned by the map's per-pane
      // renderer cache — removing the group leaves it (and its
      // viewport-sized canvas) on the map forever. Leaked canvases pile up
      // ~35MB of GPU memory per region visit until the compositor starts
      // dropping tiles: white squares over the whole UI.
      renderer.remove();
    };
  }, [map, data, focusBereich, showAllVineyards, mode]);
  return null;
}

// Lasso integration. Toggle from outside via the `lassoArmed` prop.
// Emits `onLassoFinished(ids[])` when the user closes the lasso.
function LassoController({ armed, onFinished }) {
  const map = useMap();
  const lassoRef = useRef(null);

  useEffect(() => {
    const lasso = L.lasso(map, { intersect: true });
    lassoRef.current = lasso;

    const handler = (event) => {
      // Toggle-aware: hidden vineyards (key-only filter, bereich focus,
      // out-of-scope off) are hidden via style — fillOpacity/opacity 0 in
      // layer.options is the single source of truth of visibility, so an
      // invisible polygon can never be lassoed regardless of which toggle
      // hid it.
      const ids = event.layers
        .filter(layer => (layer.options?.fillOpacity ?? 0) > 0 || (layer.options?.opacity ?? 0) > 0)
        .map(layer => layer.feature?.properties?.id)
        .filter(Boolean);
      onFinished(ids);
    };
    map.on('lasso.finished', handler);
    return () => { map.off('lasso.finished', handler); };
  }, [map, onFinished]);

  useEffect(() => {
    const lasso = lassoRef.current;
    if (!lasso) return;
    if (armed) lasso.enable();
    else lasso.disable();
  }, [armed]);

  return null;
}

let REGION_PREVIEWS = null;   // module cache: bounds.json is static per deploy
function useRegionPreviews() {
  const [previews, setPreviews] = useState(REGION_PREVIEWS);
  useEffect(() => {
    if (REGION_PREVIEWS) return;
    fetch('/region-previews/bounds.json')
      .then(r => (r.ok ? r.json() : null))
      .then(j => { REGION_PREVIEWS = j; setPreviews(j); })
      .catch(() => {});
  }, []);
  return previews;
}

export default function MapView({
  mode,
  studyCentered,
  hideVineyards,
  tripPlan,
  onBasemapReady,
  data: rawData,
  dataVersion,
  loading,
  loadError,
  onRetry,
  hoveredRegion,
  onHoverRegion,
  hoveredVillage,
  hoveredVineyardId, matchHighlightIds,
  flyTours,
  onStartFlythrough,
  draw,
  splitSession,
  onSplitAddPoint,
  onSplitUndoPoint,
  onSplitFinishLine,
  flurbActive,
  flurbProcedures,
  flurbOverlaps,
  selectedFlurbOid,
  onSelectFlurb,
  rlpLagenActive,
  rlpLagenFC,
  addedLageIds,
  selectedLageId,
  onSelectLage,
  producersLayer,
  keyProducers,
  onSelectKeyProducer,
  hoveredKeyProducerId,
  producerFilterIds,
  geoLayer,
  onSelectGeoFeature,
  selectedGeoFeature,
  overviewFitSignal,
  selectedId,
  selectedIds,
  onSelectVineyard,
  onSelectRegion,
  onLassoFinished,
  lassoArmed,
  focusBereich,
  onFocusBereich,
  onSelectVillage,
  villageEditMode,
  showVillages,
  showProducers,
  zoomToSelection,
  selectedVillage,
  onSelectProducer,
  selectedProducer,
  labelEditMode,
  regionLabelEditMode,
  geoLabelEditMode,
  riverLabelEditMode,
  mtnIconEditMode,
  geomEdit,
  geomEditRingValid,
  onGeomVertexMoved,
  onGeomVertexInserted,
  onGeomVerticesDeleted,
  showLabels,
  showAllVineyards,
  outOfScopeData,
  mapZoom,
  satellite,
  terrain,
  climateLayer,
  tempRegion,
  precipRegion,
  frankenMap,
  lagenBounds,
  onLagenBoundsChange,
  lagenAdjustable,
  onZoomChange,
  skipFitToData,
  flyToPoint,
  onFlyToComplete,
  previewFeature
}) {
  // Rivers & Mountains view active (prop-derived; hoisted above every
  // effect that lists it as a dependency — deps evaluate during render)
  const geoActive = geoLayer && mode.kind === 'regions' && !satellite && !frankenMap;
  const producersActive = producersLayer && mode.kind === 'regions';
  // Both feature views put the regions into the same grey backdrop with
  // the small grey labels; only the geo view has movable labels/obstacles.
  const greyBackdrop = geoActive || producersActive;

  const [mask, setMask] = useState(null);
  const [climateMeta, setClimateMeta] = useState(null);

  // Manual ?basemap=carto wins outright; otherwise start on the self-hosted
  // basemap and drop to Carto only if the load-time reachability probe fails
  // (the GL layers mount immediately — a healthy tileset pays no wait).
  const [cartoFallback, setCartoFallback] = useState(CARTO_FORCED || !WEBGL_OK);
  useEffect(() => {
    if (CARTO_FORCED || !WEBGL_OK) return undefined;
    let alive = true;
    probeSelfHostedTiles().then((ok) => {
      if (alive && !ok) setCartoFallback(true);
    });
    return () => { alive = false; };
  }, []);
  // Clicking the temp legend flips its units between Celsius and Fahrenheit
  const [tempInF, setTempInF] = useState(false);

  // ── Monthly precipitation animation ──
  // Starts paused on January; play loops through the months, and using the
  // slider pauses until the user hits play again.
  const [precipMonth, setPrecipMonth] = useState(0);
  const [precipPlaying, setPrecipPlaying] = useState(false);
  useEffect(() => {
    if (climateLayer !== 'precip') return;
    setPrecipMonth(0);
    setPrecipPlaying(false);
    // Preload all 12 month images so the loop swaps without flicker
    for (const src of precipRegion?.months ?? []) {
      const img = new window.Image();
      img.src = src;
    }
  }, [climateLayer, precipRegion]);
  useEffect(() => {
    if (climateLayer !== 'precip' || !precipPlaying || !precipRegion) return;
    const t = setInterval(() => setPrecipMonth(m => (m + 1) % 12), 600);
    return () => clearInterval(t);
  }, [climateLayer, precipPlaying, precipRegion]);
  // Lagen overlay fine-tuning (logged-in): drag moves the overlay, not the map
  const [lagenAdjusting, setLagenAdjusting] = useState(false);
  useEffect(() => { if (!frankenMap) setLagenAdjusting(false); }, [frankenMap]);

  useEffect(() => {
    const ctl = new AbortController();
    fetchCountries({ signal: ctl.signal })
      .then(d => setMask(d.mask))
      .catch(e => { if (e.name !== 'AbortError') console.error(e); });
    return () => ctl.abort();
  }, []);

  // Overlay bounds + legend definition, generated by scripts/make_climate_layers.py.
  // Fetched lazily the first time a climate layer is switched on.
  useEffect(() => {
    if (!climateLayer || climateMeta) return;
    const ctl = new AbortController();
    fetch('/climate/climate-meta.json', { signal: ctl.signal })
      .then(r => r.json())
      .then(setClimateMeta)
      .catch(e => { if (e.name !== 'AbortError') console.error(e); });
    return () => ctl.abort();
  }, [climateLayer, climateMeta]);


  // Active climate overlay (temp/sun) from the Germany-wide climate-meta. Temp
  // swaps to a high-res per-region image when one exists for the current region
  // (same ramp/legend, just finer data).
  const climateOverlay = (() => {
    if (!climateLayer || satellite) return null;
    if (climateLayer === 'precip') {
      if (!precipRegion) return null;
      return {
        file: precipRegion.months[precipMonth],
        bounds: precipRegion.bounds,
        legend: precipRegion
      };
    }
    const l = climateMeta?.layers?.[climateLayer];
    if (!l) return null;
    if (climateLayer === 'temp' && tempRegion) {
      return { file: tempRegion.file, bounds: tempRegion.bounds, legend: l };
    }
    return { file: l.file, bounds: climateMeta.bounds, legend: l };
  })();

  // ── Styles ────────────────────────────────────────────────────────────
  // Full-strength from the overview zoom in; dissolved when zoomed far out,
  // where the mask covers the whole Mercator world and reads as a solid
  // pink square over the faded world backdrop.
  const maskOpacity = mapZoom >= 6 ? 0.4 : mapZoom <= 4 ? 0.1 : 0.1 + (mapZoom - 4) * 0.15;
  const maskStyle = { fillColor: '#E8DAEC', fillOpacity: maskOpacity, color: 'transparent', weight: 0 };

  const REGION_DIM = '#9ca3af';
  const regionsLayerRef = useRef(null);
  // Grace period on mouseout: crossing the gap between region blobs (or a
  // polygon's own label) must not flash everything back to full color. A
  // time deferral instead of a geometric buffer — bordering regions stay
  // exact, because the next mouseover cancels the pending clear instantly.
  const regionHoverTimer = useRef(null);
  const regionHoverSet = useCallback((name) => {
    clearTimeout(regionHoverTimer.current);
    onHoverRegion?.(name);
  }, [onHoverRegion]);
  const regionHoverClear = useCallback(() => {
    clearTimeout(regionHoverTimer.current);
    regionHoverTimer.current = setTimeout(() => onHoverRegion?.(null), 700);
  }, [onHoverRegion]);
  useEffect(() => () => clearTimeout(regionHoverTimer.current), []);
  // Restyle in place on hover — remounting the layer per hover would be
  // janky, and react-leaflet GeoJSON only reads `style` at mount.
  useEffect(() => {
    const lg = regionsLayerRef.current;
    if (!lg || mode.kind !== 'regions') return;
    lg.eachLayer(l => {
      const name = l.feature?.properties?.name;
      if (!name || !l.setStyle) return;
      // grey-backdrop views: the hovered region lights up in its full
      // color; everything else stays subdued
      if (greyBackdrop) {
        if (hoveredRegion && name === hoveredRegion) {
          const c = colorForRegion(name);
          l.setStyle({ fillColor: c, color: c, fillOpacity: 0.32, weight: 1.4, opacity: 1 });
        } else {
          l.setStyle({ fillColor: '#8f8a84', color: '#8f8a84', fillOpacity: 0.14, weight: 0.7, opacity: 0.4 });
        }
        return;
      }
      const dim = !!hoveredRegion && name !== hoveredRegion;
      const c = dim ? REGION_DIM : colorForRegion(name);
      l.setStyle({ fillColor: c, color: c });
    });
  }, [hoveredRegion, mode.kind, dataVersion, greyBackdrop]);

  const regionPolygonStyle = (feature) => {
    const name = feature.properties?.name;
    const hasGeom = !!feature.properties?.has_geometry;
    // Rivers & Mountains / Key Producers views: uniform quiet grey,
    // hairline borders — the regions are backdrop only there (and inert
    // via the map-shell modifier CSS)
    if (greyBackdrop) {
      return {
        fillColor: '#8f8a84',
        fillOpacity: hasGeom ? 0.14 : 0,
        color: '#8f8a84',
        weight: hasGeom ? 0.7 : 0,
        opacity: 0.4,
        dashArray: null,
      };
    }
    const c = name ? colorForRegion(name) : UNCLASSIFIED_COLOR;
    return {
      fillColor: c,
      fillOpacity: hasGeom ? 0.32 : 0,
      color: c,
      weight: hasGeom ? 1.4 : 0,
      dashArray: hasGeom ? null : '4 4'
    };
  };

  const vineyardStyle = (feature) => {
    const id = feature.properties.id;
    if (!id) return { fillOpacity: 0, opacity: 0, weight: 0 };
    const bereich = feature.properties.bereich;
    // Hide vineyards outside the focused bereich
    if (focusBereich && bereich !== focusBereich) return { fillOpacity: 0, opacity: 0, weight: 0 };
    const isSel = id === selectedId || selectedIds.has(id) || id === hoveredVineyardId
      || (matchHighlightIds != null && matchHighlightIds.has(id));
    const region = feature.properties.anbaugebiet;
    const notable = !!feature.properties.notable;
    const isKey = isKeyVineyard(feature.properties);
    // Hide non-key vineyards when filter is active (but always show selected)
    if (!showAllVineyards && !isKey && !isSel) return { fillOpacity: 0, opacity: 0, weight: 0 };
    // Flurbereinigung layer: every visible vineyard renders uniform green,
    // overriding all status-based coloring (spec). Selection keeps its dark
    // outline for orientation; the fill stays green.
    if (flurbActive || rlpLagenActive) {
      // RLP Lagen view: gold (notable) and black (VDP-GG) vineyards keep
      // their status colors; everything else reads uniform green.
      let fillColor = '#22c55e', border = '#15803d';
      if (rlpLagenActive) {
        if (notable) { fillColor = NOTABLE_GOLD; border = NOTABLE_GOLD; }
        else if (isVdpGgBlack(feature.properties)) { fillColor = VDP_GG_BLACK; border = VDP_GG_BLACK; }
      }
      const flurbFill = satellite ? 0.3 : fillColor === VDP_GG_BLACK ? 0.34 : 0.45;
      return {
        fillColor,
        fillOpacity: isSel ? flurbFill : fillOpacityForZoom(mapZoom, flurbFill, satellite ? 0.6 : 0.85),
        color: isSel ? '#4a4a4a' : border,
        weight: isSel ? 2 : Math.max(borderWeightForZoom(mapZoom, 0.5), dotWeightForZoom(mapZoom)),
        dashArray: null,
      };
    }
    const verified = !!feature.properties.last_verified;

    // Fill color: gold for GWS/notable, black for VDP-GG-not-GWS, bereich color otherwise
    const isBlack = isVdpGgBlack(feature.properties);
    const baseColor = mode.kind === 'region' && bereich
      ? colorForBereich(bereich, region)
      : region ? colorForRegion(region) : UNCLASSIFIED_COLOR;
    const fillColor = notable ? NOTABLE_GOLD : isBlack ? VDP_GG_BLACK : baseColor;

    // Fill opacity: verified reads a notch more solid than unverified, but
    // both stay translucent enough that the base map shows through.
    let fillOpacity = isSel ? 0.9 : verified ? 0.55 : 0.4;
    // Custom polygons render slightly more solid than OSM/relation ones, but
    // only once zoomed in far enough that the GWS/GG labels appear (zoom >= 12).
    const isCustom = feature.properties.osm_type === 'custom';
    if (isCustom && mapZoom >= 12) fillOpacity = Math.min(1, fillOpacity * 1.2);
    // Over satellite imagery, fade fills so the terrain stays visible and
    // lean on slightly stronger borders instead.
    if (satellite) fillOpacity = isSel ? 0.45 : 0.18;
    // Black VDP-GG fills read very heavy at full strength; hold them to a
    // softer opacity (overriding the verified/custom boost) so they don't
    // dominate the map.
    if (isBlack && !isSel) fillOpacity = satellite ? 0.15 : verified ? 0.34 : 0.26;

    // Border: matches fill color; selected gets dark gray, preserving dash
    const dashArray = verified ? null : dashForZoom(mapZoom, '4 3');

    // As borders ramp away below zoom 13, fills strengthen so region-level
    // vineyard density stays readable (satellite keeps more imagery through).
    if (!isSel) fillOpacity = fillOpacityForZoom(mapZoom, fillOpacity, satellite ? 0.6 : 0.85);

    return {
      fillColor,
      fillOpacity,
      color: isSel ? '#4a4a4a' : fillColor,
      weight: isSel ? 2 : Math.max(borderWeightForZoom(mapZoom, satellite ? 0.9 : 0.5), dotWeightForZoom(mapZoom)),
      dashArray
    };
  };

  // Out-of-scope vineyards always render uniform white, regardless of
  // region/bereich. Hidden outside a focused bereich to match the main layer.
  // Selected (single or lasso) gets the same dark-gray highlight border as
  // the main layer so they select and bulk-edit like any other vineyard.
  const outOfScopeStyle = (feature) => {
    if (focusBereich && feature.properties?.bereich !== focusBereich) {
      return { fillOpacity: 0, opacity: 0, weight: 0 };
    }
    const id = feature.properties?.id;
    const isSel = !!id && (id === selectedId || selectedIds.has(id));
    if (isSel) {
      return satellite
        ? { fillColor: '#ffffff', fillOpacity: 0.45, color: '#4a4a4a', weight: 2 }
        : { fillColor: '#ffffff', fillOpacity: 0.9, color: '#4a4a4a', weight: 2 };
    }
    return satellite
      ? { fillColor: '#ffffff', fillOpacity: 0.2, color: '#e5e7eb', weight: borderWeightForZoom(mapZoom, 0.5) }
      : { fillColor: '#ffffff', fillOpacity: 0.7, color: '#9ca3af', weight: borderWeightForZoom(mapZoom, 0.3) };
  };

  // Remounts the white layer when its data, the focused bereich, the base
  // layer, or the selection changes (react-leaflet GeoJSON only reads
  // data/style on mount).
  const outOfScopeKey = `oos-${mode.name ?? ''}-${outOfScopeData?.count ?? 0}-${focusBereich ?? ''}-${satellite ? 'sat' : 'map'}-${selectedId ?? ''}-${selectedIds.size}-bw${Math.round(mapZoom)}`;


  // Key that forces GeoJSON remount whenever data or selection changes.
  // react-leaflet GeoJSON only reads data on mount, so we must remount it.
  const geoKey = `${mode.kind}-${mode.name ?? ''}-v${dataVersion}-${selectedId ?? ''}-${selectedIds.size}-${lassoArmed}-${flurbActive ? 'fb' : ''}-${rlpLagenActive ? 'rlp' : ''}-${greyBackdrop ? 'grey' : ''}`;

  const onGeoRiverHit = useCallback((f) => {
    const raw = f.properties?.['name:de'] ?? f.properties?.name;
    const name = RIVER_ALIASES[raw] ?? raw; // French border segments -> Rhein
    if (name && KEY_RIVERS.includes(name)) onSelectGeoFeature({ kind: 'river', name });
  }, [onSelectGeoFeature]);

  // Selection dimming: everything except the selected feature subdues —
  // other rivers when a river is picked, ALL rivers when a mountain is
  // picked (and GeoMarkers mirrors this for icons and labels). A selected
  // river matches its alias-named segments too (Rhein's border stretch).
  const geoRiverPaint = useMemo(() => {
    const sel = selectedGeoFeature;
    const riverSel = sel?.kind === 'river' ? sel.name : null;
    const selNames = riverSel
      ? [riverSel, ...Object.keys(RIVER_ALIASES).filter((a) => RIVER_ALIASES[a] === riverSel)]
      : null;
    const lineOp = !sel ? 0.85
      : selNames ? ['match', RIVER_NAME, selNames, 0.85, 0.22] : 0.22;
    const textOp = !sel ? 1
      : selNames ? ['match', RIVER_NAME, selNames, 1, 0.3] : 0.3;
    return {
      'geo-river-line-low': { 'line-opacity': lineOp },
      'geo-river-line': { 'line-opacity': lineOp },
      'geo-river-label': { 'text-opacity': textOp, 'text-halo-width': sel ? 1.2 : 1.6 },
    };
  }, [selectedGeoFeature]);

  // All vineyard polygons (ways, relations, custom) render in a single pane.
  // SVG stacking follows insertion order, so the sort determines what sits on
  // top: key vineyards (VDP GG / GWS eligible, notable) always render above
  // the rest, and within each group larger polygons render below smaller
  // ones. Polygons hidden by the stack are reachable via click drill-down
  // (see drillTargetAt).
  // Provenance gate: useMapData keeps the previous view's FeatureCollection
  // until the new one arrives, but rendering it is what made the old region
  // linger over the new region's placeholder during swaps. A region view
  // renders only its own data; the overview/unassigned views never render a
  // region's vineyards. (FitToData still gets rawData: its fallback zooms
  // from the stale overview FC when no preview bounds exist.)
  const dataIsStale = mode.kind === 'region'
    ? rawData?.region !== mode.name
    : !!rawData?.region;
  const data = dataIsStale ? null : rawData;

  const regionPreviews = useRegionPreviews();
  // Region fetch failed with nothing (fresh or cached) to show: keep the
  // status pill up with a retry, grey the PNG so it doesn't read as loaded.
  const regionLoadFailed = mode.kind === 'region' && !loading && !!loadError && data?.region !== mode.name;

  // Memoised on `data` so CulledVineyardLayer doesn't see a new `data` reference on every parent render.
  const sortedData = useMemo(() => {
    if (!data?.features) return null;
    const isCustom = (f) => f.properties?.osm_type === 'custom' ? 1 : 0;
    // Stacking tier (higher renders on top): plain < other-key/black < gold.
    const tier = (f) => {
      const p = f.properties || {};
      if (p.notable) return 2;
      if (p.vdp_gg_eligible || p.official_grosses_gewaechs_eligible) return 1;
      return 0;
    };
    // Within each key tier, custom polygons render above OSM ways/relations
    // regardless of size: plain OSM < plain custom < black OSM < black
    // custom < gold OSM < gold custom; larger below smaller within a rank.
    const rank = (f) => tier(f) * 2 + isCustom(f);
    const features = data.features
      .map(f => [featureArea(f), f])
      .sort((a, b) => (rank(a[1]) - rank(b[1])) || (b[0] - a[0]))
      .map(([, f]) => f);
    return { ...data, features };
  }, [data]);

  // Out-of-scope features join the click drill-down whenever their layer is
  // visible. Their pane renders below the main one, so they come first here
  // (drillTargetAt scans last-rendered = topmost first).
  const oosFeatures = (mode.kind === 'region' && outOfScopeData?.features?.length > 0)
    ? outOfScopeData.features : null;
  const drillFeatures = useMemo(() => {
    if (!oosFeatures) return sortedData?.features ?? null;
    return [...oosFeatures, ...(sortedData?.features ?? [])];
  }, [oosFeatures, sortedData]);
  const oosIds = useMemo(
    () => new Set((oosFeatures ?? []).map(f => f.properties?.id).filter(Boolean)),
    [oosFeatures]
  );

  // Bbox index for fast point-in-polygon prefiltering during click drill-down.
  const drillBboxes = useMemo(() => {
    const m = new Map();
    for (const f of drillFeatures ?? []) {
      const id = f.properties?.id;
      if (id) m.set(id, featureBbox(f));
    }
    return m;
  }, [drillFeatures]);

  // Click handlers are bound once per Leaflet layer, so they read the current
  // selection/filter state through this ref.
  const drillRef = useRef(null);
  drillRef.current = { selectedId, selectedIds, focusBereich, showAllVineyards, features: drillFeatures, bboxes: drillBboxes, oosIds, matchIds: matchHighlightIds };

  // Right-click on stacked polygons: an at-cursor menu of every layer under
  // the point (top to bottom of the drill stack); picking one selects it.
  // Out-of-scope polygons appear exactly when their toggle is on — the same
  // gate that put them in drillFeatures.
  const [ctxMenu, setCtxMenu] = useState(null);
  const ctxMenuElRef = useRef(null);
  const openOverlapMenu = (e) => {
    L.DomEvent.stopPropagation(e);
    e.originalEvent?.preventDefault();
    const ctx = drillRef.current;
    const stack = drillStackAt(e.latlng, ctx);
    if (stack.length === 0) return;
    const byId = new Map((ctx.features ?? []).map(f => [f.properties?.id, f.properties]));
    setCtxMenu({
      x: e.containerPoint.x,
      y: e.containerPoint.y,
      items: stack.map(id => {
        const p = byId.get(id) ?? {};
        return { id, name: p.canonical_name_de || p.einzellage || 'Unnamed vineyard' };
      }),
    });
  };
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (ev) => { if (!ctxMenuElRef.current?.contains(ev.target)) setCtxMenu(null); };
    const onKey = (ev) => { if (ev.key === 'Escape') setCtxMenu(null); };
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('wheel', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close, true);
      document.removeEventListener('wheel', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);
  // View swaps invalidate the menu's coordinates and its stack alike.
  useEffect(() => { setCtxMenu(null); }, [mode.kind, mode.name, focusBereich]);

  // ── Mode-specific layer rendering ─────────────────────────────────────
  let mainLayer = null;
  if (data && mode.kind === 'regions') {
    // Nested regions (michigan: Fennville inside Lake Michigan Shore) must
    // paint ABOVE their parent or the parent's fill hides them and steals
    // their hover — draw largest first so smaller regions land on top.
    // Order is irrelevant where regions don't overlap (Germany).
    const approxArea = (f) => {
      let sum = 0;
      const polys = f.geometry?.type === 'Polygon' ? [f.geometry.coordinates]
        : f.geometry?.type === 'MultiPolygon' ? f.geometry.coordinates : [];
      for (const p of polys) {
        const ring = p[0] ?? [];
        for (let i = 0; i < ring.length - 1; i++) {
          sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
        }
      }
      return Math.abs(sum);
    };
    const ordered = { ...data, features: [...(data.features ?? [])].sort((x, y) => approxArea(y) - approxArea(x)) };
    mainLayer = (
      <GeoJSON
        key={geoKey}
        ref={regionsLayerRef}
        data={ordered}
        pane="regions-pane"
        style={regionPolygonStyle}
        onEachFeature={(f, layer) => {
          const name = f.properties?.name;
          if (!name) return;
          layer.on('click', () => onSelectRegion(name));
          if (!isMobile()) {
            layer.on('mouseover', () => regionHoverSet(name));
            layer.on('mouseout', regionHoverClear);
          }
        }}
      />
    );
  } else if (data) {
    const vineyardEachFeature = (f, layer) => {
      const id = f.properties?.id;
      if (!id) return;
      layer.on('click', (e) => {
        if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
        // Every vineyard layer runs the same stack logic, so it doesn't
        // matter which polygon Leaflet delivers the click to.
        onSelectVineyard(drillTargetAt(e.latlng, drillRef.current) ?? id);
      });
      layer.on('contextmenu', (e) => {
        if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
        openOverlapMenu(e);
      });
    };
    mainLayer = (
      <CulledVineyardLayer
        data={sortedData || data}
        selectedId={selectedId}
        selectedIds={selectedIds}
        labelZoomActive={mapZoom >= 12}
        satellite={satellite}
        showAllVineyards={showAllVineyards}
        focusBereich={focusBereich}
        pane="vineyards-pane"
        style={vineyardStyle}
        onEachFeature={vineyardEachFeature}
        styleSignal={`${rlpLagenActive ? 'rlp' : flurbActive ? 'flurb' : ''}-hv${hoveredVineyardId ?? ''}-bw${Math.round(mapZoom * 2)}-mh${matchHighlightIds ? `${matchHighlightIds.size}:${matchHighlightIds.values().next().value ?? ''}` : ''}`}
      />
    );
  }

  return (
    <div className={`map-shell ${producersLayer && mode.kind === 'regions' ? 'map-shell--producers' : ''} ${geoActive ? 'map-shell--geo' : ''}`}>
      {/* SVG pattern definition for unverified vineyard stripes */}
      {/* minZoom stops before the Mercator world edge enters the viewport —
          beyond it the container background shows as a pink frame */}
      <MapContainer
        center={[49.8, 9.5]}
        zoom={7}
        minZoom={4}
        maxZoom={22}
        zoomControl={false}
        worldCopyJump={false}
        style={{ height: '100%', width: '100%', background: '#f5f0f7' }}
      >
        <MapReady>
        <FitToGermany centered={studyCentered} />
        <FitOverviewOnSignal signal={overviewFitSignal} />
        <InvalidateOnTheme />
        <FlyToPoint point={flyToPoint} onComplete={onFlyToComplete} />
        <FitToData data={rawData} mode={mode} dataVersion={dataVersion} skip={skipFitToData} previewBounds={mode.kind === 'region' ? regionPreviews?.[mode.name]?.bounds : null} />
        {mode.kind === 'region' && <FitToBereich data={data} mode={mode} focusBereich={focusBereich} selectedId={selectedId} selectedVillage={selectedVillage} selectedProducer={selectedProducer} />}
        <FitToSelection data={data} selectedId={selectedId} zoomToSelection={zoomToSelection} />
        <FitToVillage village={selectedVillage} zoomToSelection={zoomToSelection} />
        <FitToVillage village={selectedProducer} zoomToSelection={zoomToSelection} />
        <ZoomControl position="bottomright" />
        <StaticAttribution carto={cartoFallback} />
        {frankenMap ? null : satellite ? (
          <TileLayer
            key="tiles-sat"
            url={ATLAS.satelliteUrl}
            maxNativeZoom={ATLAS.satelliteMaxNativeZoom}
            maxZoom={22}
            keepBuffer={5}
            updateWhenZooming={false}
          />
        ) : cartoFallback ? (
          <TileLayer
            key="tiles-map"
            url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
            maxNativeZoom={20}
            maxZoom={22}
            keepBuffer={5}
            updateWhenZooming={false}
          />
        ) : (
          <Suspense fallback={null}>
            <BasemapGL key="tiles-gl" style={BASEMAP_STYLE} pane="tilePane" onFirstIdle={onBasemapReady} />
          </Suspense>
        )}

        <Pane name="lagen"           style={{ zIndex: 150 }} />
        <Pane name="hillshade"       style={{ zIndex: 240, mixBlendMode: 'multiply' }} />
        <Pane name="town-labels"     style={{ zIndex: 260 }} />
        <Pane name="mask" style={{ zIndex: 250 }} />
        <Pane name="regions-pane"    style={{ zIndex: 320 }} />
        <Pane name="region-outline"  style={{ zIndex: 390 }} />
        <Pane name="lakes"           style={{ zIndex: 310 }} />
        <Pane name="climate"         style={{ zIndex: 340 }} />
        <Pane name="flurb"           style={{ zIndex: 330 }} />
        <Pane name="region-preview"  style={{ zIndex: 390 }} />
        <Pane name="rlp-lagen"       style={{ zIndex: 405 }} />
        <Pane name="flurb-overlap"   style={{ zIndex: 410 }} />
        <Pane name="out-of-scope"    style={{ zIndex: 360 }} />
        <Pane name="labels"          style={{ zIndex: 500 }} />
        <Pane name="vineyards-pane"  style={{ zIndex: 400 }} />
        <Pane name="notable-labels"   style={{ zIndex: 440 }} />
        <Pane name="villages"        style={{ zIndex: 450 }} />
        <Pane name="producers"       style={{ zIndex: 455 }} />
        {/* pointer-events none: the GL pane sits above the labels pane and
            would otherwise swallow drags meant for the region labels —
            river clicks arrive via map-level hit-testing, not this pane */}
        <Pane name="geo-rivers"      style={{ zIndex: 505, pointerEvents: 'none' }} />
        <Pane name="geo-markers"     style={{ zIndex: 506 }} />

        {/* town/city names split from the basemap (labels-only style /
            light_only_labels): quarter opacity, above hillshade so Terrain
            keeps names, below all vineyard content. Must render AFTER the
            pane declarations. */}
        {!frankenMap && !satellite && (cartoFallback ? (
          <TileLayer
            key="tiles-map-labels"
            url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
            pane="town-labels"
            opacity={0.25}
            maxNativeZoom={20}
            maxZoom={22}
            keepBuffer={5}
            updateWhenZooming={false}
          />
        ) : (
          <Suspense fallback={null}>
            <BasemapGL key="tiles-gl-labels" style={BASEMAP_LABELS_STYLE} pane="town-labels" opacity={0.275} />
          </Suspense>
        ))}

        {/* The world backdrop outside Germany is baked into the basemap
            style itself: own Natural Earth tiles (countries, borders,
            major rivers, top cities) under the Germany land fill, dimmed
            by the lavender country mask. */}

        {/* Terrain: multidirectional hillshade multiplied over the map. Flat
            ground stays light; steep slopes darken and pop. Not clipped to
            Germany — an SVG clip-path on the pane renders it blank on mobile
            Safari/Chrome; the country mask above dims relief outside Germany. */}
        {terrain && !satellite && (
          <TileLayer
            key="tiles-hillshade"
            url={ATLAS.hillshadeUrl}
            pane="hillshade"
            opacity={0.9}
            maxNativeZoom={15}
            maxZoom={22}
            keepBuffer={5}
            updateWhenZooming={false}
          />
        )}

        <MaskLayer mask={mask} style={maskStyle} />
        <PreviewLayer feature={previewFeature} />

        {frankenMap && (
          <LagenOverlay bounds={lagenBounds} adjusting={lagenAdjusting} onAdjusted={onLagenBoundsChange} />
        )}

        {/* Climate overlays (temp/sun) pair with the map basemap, never satellite */}
        {climateOverlay && (
          <ImageOverlay
            key={climateLayer === 'precip'
              ? `precip-${mode.name ?? ''}`   /* stable: month swaps via setUrl, no remount flicker */
              : `${climateLayer}-${climateOverlay.file}`}
            url={climateOverlay.file}
            bounds={climateOverlay.bounds}
            opacity={0.65}
            pane="climate"
          />
        )}

        {mode.kind === 'region' && !hideVineyards && outOfScopeData?.features?.length > 0 && (
          <GeoJSON
            key={outOfScopeKey}
            data={outOfScopeData}
            pane="out-of-scope"
            style={outOfScopeStyle}
            onEachFeature={(f, layer) => {
              const id = f.properties?.id;
              if (!id) return;
              layer.on('click', (e) => {
                if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
                // Same stack cycling as the main layer, so overlapping
                // in-scope/out-of-scope polygons drill through consistently.
                onSelectVineyard(drillTargetAt(e.latlng, drillRef.current) ?? id);
              });
              layer.on('contextmenu', (e) => {
                if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
                openOverlapMenu(e);
              });
            }}
          />
        )}
        {/* RLP Weinbergsrolle Einzellagen (local-only layer): official
            register polygons not yet in the app render blue ABOVE the
            vineyard layer (z 405 > 400): where they overlap, the blue
            polygon wins the click.
            Already-imported ones (tags.rlp_lage_id) are filtered out. */}
        {rlpLagenActive && rlpLagenFC?.features?.length > 0 && (
          <GeoJSON
            key={`rlp-lagen-${mode.name ?? 'all'}-${addedLageIds?.size ?? 0}-${selectedLageId ?? ''}`}
            data={{
              type: 'FeatureCollection',
              features: rlpLagenFC.features.filter(f =>
                (mode.kind !== 'region' || f.properties?.anbaugebiet === mode.name)
                && !addedLageIds?.has(String(f.properties?.id))),
            }}
            pane="rlp-lagen"
            style={(f) => (f.properties?.id === selectedLageId
              ? { fillColor: '#3b82f6', fillOpacity: 0.55, color: '#4a4a4a', weight: 2 }
              : { fillColor: '#3b82f6', fillOpacity: 0.35, color: '#1d4ed8', weight: 1 })}
            onEachFeature={(f, layer) => {
              layer.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                onSelectLage?.(f);
              });
            }}
          />
        )}

        {/* Flurbereinigung: gray procedures below the vineyards (green while
            active), red server-computed overlaps above them; both clickable
            to the procedure sidebar. */}
        {flurbActive && flurbProcedures?.features?.length > 0 && (
          <GeoJSON
            key={`flurb-${flurbProcedures.features.length}-${mode.name ?? 'all'}-${focusBereich ?? ''}-${selectedFlurbOid ?? ''}`}
            data={flurbProcedures}
            pane="flurb"
            style={(f) => (f.properties?.oid === selectedFlurbOid
              // only the selected procedure gets the heavy dark border
              ? { fillColor: '#9ca3af', fillOpacity: 0.5, color: '#4a4a4a', weight: 2 }
              : { fillColor: '#9ca3af', fillOpacity: 0.35, color: '#6b7280', weight: 1 })}
            onEachFeature={(f, layer) => {
              const oid = f.properties?.oid;
              if (!oid) return;
              layer.on('click', (e) => {
                if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
                L.DomEvent.stopPropagation(e);
                onSelectFlurb(oid);
              });
            }}
          />
        )}
        {flurbActive && flurbOverlaps?.features?.length > 0 && (
          <GeoJSON
            key={`flurbov-${flurbOverlaps.features.length}-${mode.name ?? 'all'}-${focusBereich ?? ''}-${selectedFlurbOid ?? ''}`}
            data={flurbOverlaps}
            pane="flurb-overlap"
            style={(f) => (f.properties?.flurb_oid === selectedFlurbOid
              ? { fillColor: '#dc2626', fillOpacity: 0.7, color: '#4a4a4a', weight: 2 }
              : { fillColor: '#dc2626', fillOpacity: 0.6, color: '#991b1b', weight: 1 })}
            onEachFeature={(f, layer) => {
              const oid = f.properties?.flurb_oid;
              if (!oid) return;
              layer.on('click', (e) => {
                if (lassoArmed) { L.DomEvent.stopPropagation(e); return; }
                L.DomEvent.stopPropagation(e);
                onSelectFlurb(oid);
              });
            }}
          />
        )}
        {/* instant region placeholder: pre-rendered PNG under the vineyard
            pane while the live GeoJSON loads (default view only) */}
        {mode.kind === 'region' && !hideVineyards && regionPreviews?.[mode.name] && showAllVineyards && !focusBereich && (
          <RegionPreviewOverlay
            key={`preview-${mode.name}`}
            info={regionPreviews[mode.name]}
            active={data?.region !== mode.name}
            failed={regionLoadFailed}
          />
        )}
        {hideVineyards ? null : mainLayer}
        {data && mode.kind !== 'regions' && !hideVineyards && (
          <DensityDotsLayer
            data={sortedData || data}
            focusBereich={focusBereich}
            showAllVineyards={showAllVineyards}
            mode={mode}
          />
        )}
        {mode.kind === 'region' && ATLAS.features.regionOutline && <RegionOutline region={mode.name} />}
        {ATLAS.clickableLakes?.length > 0 && <ClickableLakes onSelect={onSelectGeoFeature} />}
        {mode.kind === 'regions' && data && <RegionLabels data={data} onSelectRegion={onSelectRegion} canEdit={geoActive ? geoLabelEditMode : producersActive ? false : regionLabelEditMode} geoView={greyBackdrop} geoObstacles={geoActive} hoveredRegion={hoveredRegion} onHoverSet={regionHoverSet} onHoverClear={regionHoverClear} />}
        {mode.kind === 'regions' && producersLayer && <KeyProducerMarkers producers={keyProducers} onSelect={onSelectKeyProducer} hoveredId={hoveredKeyProducerId} filterIds={producerFilterIds} />}
        {/* Key Rivers & Mountains: tile-derived river highlight + labels
            (clickable via GL hit-test, no new polygons) and app-placed
            mountain icons/labels. River label markers carry the overview
            zooms and hand over to the GL line labels from z9. */}
        {geoActive && !cartoFallback && (
          <Suspense fallback={null}>
            <BasemapGL
              key="geo-rivers-gl"
              style={GEO_RIVERS_STYLE}
              pane="geo-rivers"
              hitLayers={GEO_RIVER_HIT_LAYERS}
              onFeatureClick={onGeoRiverHit}
              paint={geoRiverPaint}
            />
          </Suspense>
        )}
        {geoActive && <GeoMarkers onSelect={onSelectGeoFeature} selected={selectedGeoFeature} riverEdit={!!riverLabelEditMode} mtnEdit={!!mtnIconEditMode} />}
        {mode.kind === 'region' && !focusBereich && data && <BereichLabels data={data} anbaugebiet={mode.name} onFocusBereich={onFocusBereich} />}
        {mode.kind === 'region' && !hideVineyards && data && !loading && showLabels && <NotableLabels data={data} focusBereich={focusBereich} canEdit={labelEditMode} onSelectVineyard={onSelectVineyard} hoveredId={hoveredVineyardId} />}
        {onZoomChange && <ZoomTracker onZoomChange={onZoomChange} />}
        {mode.kind === 'region' && draw?.session && <DrawLayer draw={draw} />}
        {splitSession && (
          <SplitLayer
            session={splitSession}
            onAddPoint={onSplitAddPoint}
            onUndoPoint={onSplitUndoPoint}
            onFinishLine={onSplitFinishLine}
          />
        )}
        {geomEdit && <GeometryEditLayer session={geomEdit} ringValid={geomEditRingValid} onVertexMoved={onGeomVertexMoved} onVertexInserted={onGeomVertexInserted} onVerticesDeleted={onGeomVerticesDeleted} />}
        {mode.kind === 'region' && showVillages && !loading && <VillageMarkers region={mode.name} onSelectVillage={onSelectVillage} canEdit={villageEditMode} />}
        {mode.kind === 'region' && hoveredVillage && <HoverVillageDot village={hoveredVillage} />}
        {mode.kind === 'region' && !isMobile() && mapZoom >= 12 && (flyTours ?? []).map(tr => (
          <HeliportMarker key={tr.key} onStart={() => onStartFlythrough(tr)} heliport={tr.heliport} label={tr.label} />
        ))}
        {mode.kind === 'region' && hoveredVineyardId && !showLabels && <HoverVineyardLabel data={data} vineyardId={hoveredVineyardId} />}
        {mode.kind === 'region' && showProducers && !loading && (
          <ProducerMarkers region={mode.name} bereich={focusBereich || null} onSelectProducer={onSelectProducer} selectedProducerId={selectedProducer?.id ?? null} />
        )}
        {tripPlan && <TripRouteOverlay plan={tripPlan} />}

        <LassoController armed={lassoArmed} onFinished={onLassoFinished} />
        </MapReady>
      </MapContainer>

      {climateOverlay && (() => {
        if (climateLayer === 'precip') {
          const p = precipRegion;
          return (
            <div className="climate-legend">
              <div className="climate-legend__bar" style={{ background: `linear-gradient(to right, ${p.stops.join(', ')})` }} />
              <div className="climate-legend__range">
                <span>{p.min} {p.unit}</span>
                <span>{p.max} {p.unit}</span>
              </div>
              <div className="climate-legend__controls">
                <button
                  className="climate-legend__play"
                  onClick={() => setPrecipPlaying(v => !v)}
                  aria-label={precipPlaying ? 'Pause' : 'Play'}
                >
                  {precipPlaying ? (
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" aria-hidden="true"><rect x="1.5" y="1" width="3" height="10" rx="0.8" /><rect x="7.5" y="1" width="3" height="10" rx="0.8" /></svg>
                  ) : (
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" aria-hidden="true"><path d="M2.5 1.2v9.6L10.6 6 2.5 1.2z" /></svg>
                  )}
                </button>
                <input
                  className="climate-legend__slider"
                  type="range" min="0" max="11" step="1"
                  value={precipMonth}
                  aria-label="Month"
                  onChange={(e) => { setPrecipMonth(Number(e.target.value)); setPrecipPlaying(false); }}
                />
                <span className="climate-legend__month">{MONTH_NAMES[precipMonth]}</span>
              </div>
              <div className="climate-legend__footer">
                <span className="climate-legend__title">{p.label}</span>
              </div>
            </div>
          );
        }
        const l = climateOverlay.legend;
        const isTemp = climateLayer === 'temp';
        // Per-region temp uses only a slice of the full ramp, so the legend
        // shows that region's actual loaded range and the matching colors.
        const region = isTemp && tempRegion ? tempRegion : null;
        const min = region ? region.min : l.stops[0].value;
        const max = region ? region.max : l.stops[l.stops.length - 1].value;
        const gradient = region
          ? `linear-gradient(to right, ${Array.from({ length: 16 }, (_, i) => {
              const t = i / 15;
              return `${colorAt(l.stops, min + (max - min) * t)} ${(t * 100).toFixed(1)}%`;
            }).join(', ')})`
          : `linear-gradient(to right, ${l.stops
              .map(s => `${s.color} ${((s.value - l.stops[0].value) / (l.stops[l.stops.length - 1].value - l.stops[0].value) * 100).toFixed(1)}%`)
              .join(', ')})`;
        const showF = isTemp && tempInF;
        const disp = (c) => showF ? Math.round(c * 9 / 5 + 32) : c;
        const unit = showF ? '°F' : l.unit;
        return (
          <div
            className="climate-legend"
            onClick={isTemp ? () => setTempInF(v => !v) : undefined}
          >
            <div className="climate-legend__bar" style={{ background: gradient }} />
            <div className="climate-legend__range">
              <span>{disp(min)} {unit}</span>
              <span>{disp(max)} {unit}</span>
            </div>
            <div className="climate-legend__footer">
              <span className="climate-legend__title">{l.label}</span>
            </div>
          </div>
        );
      })()}


      {frankenMap && lagenAdjustable && (
        <div className="lagen-adjust">
          {lagenAdjusting ? (
            <>
              <span className="lagen-adjust__hint">Drag the map to move the overlay</span>
              <button className="lagen-adjust__btn" onClick={() => setLagenAdjusting(false)}>Done</button>
            </>
          ) : (
            <button className="lagen-adjust__btn" onClick={() => setLagenAdjusting(true)}>Adjust overlay</button>
          )}
        </div>
      )}

      {ctxMenu && (
        <div className="map-ctxmenu" ref={ctxMenuElRef} style={{ left: ctxMenu.x, top: ctxMenu.y }}>
          <ul className="dropdown__list" role="menu">
            {ctxMenu.items.map(it => (
              <li
                key={it.id}
                role="menuitem"
                className={`dropdown__item ${it.id === selectedId ? 'dropdown__item--active' : ''}`}
                onClick={() => { setCtxMenu(null); onSelectVineyard(it.id); }}
              >
                {it.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {mode.kind === 'region' && loading && (
        <div className="map-status">Loading vineyards in {mode.name}…</div>
      )}
      {mode.kind === 'region' && !loading && loadError && (
        <div className="map-error">
          Loading {mode.name} failed
          <button className="map-error__retry" onClick={onRetry}>Try again</button>
        </div>
      )}
      {mode.kind === 'region' && !loading && !loadError && data?.features && (
        <LoadedPill count={data.features.length} />
      )}
      {mode.kind === 'unassigned' && loading && (
        <div className="map-status">Loading unassigned vineyards…</div>
      )}
      {mode.kind === 'regions' && loading && (
        <div className="map-status">Loading regions…</div>
      )}
    </div>
  );
}
